import React from "react";
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  EdgeChange,
  Handle,
  MarkerType,
  MiniMap,
  Node,
  NodeChange,
  Position,
  ReactFlowInstance,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import api from "../api";

type Status = "green" | "green_strong" | "yellow" | "red" | "neutral";
type Campus = string;
type Frequency = "weekly" | "monthly" | "yearly";
type Scope = "campus" | "global";
type PerformanceDirection = "higher_is_better" | "lower_is_better";
type Lens = string;
type MetricHistory = Record<string, number | undefined>;
type LensPositions = Record<string, { x: number; y: number }>;
type ScopeType = "organization" | "campus" | "ministry" | "department" | "service";
type FloatingPanelKey = "lens" | "reference" | "kpi";
type EnterpriseSummary = {
  id: number;
  name: string;
  slug: string;
};
type LensConfig = {
  name: string;
  scopeType: ScopeType;
  scopeId: string;
  description: string;
};
type LensConfigs = Record<Lens, LensConfig>;
type WorkStatus = "not-started" | "in-progress" | "done";

type TaskItem = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  notes: string;
};

type InitiativeItem = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  summary: string;
  tasks: TaskItem[];
};

type DetailFieldKey =
  | "definition"
  | "measurement"
  | "owner"
  | "dataSource"
  | "updateNotes"
  | "eventDate";

type KpiNodeData = {
  label: string;
  target?: number;
  targetHistory: MetricHistory;
  actual?: number;
  actualHistory: MetricHistory;
  unit?: string;
  frequency: Frequency;
  scope: Scope;
  performanceDirection: PerformanceDirection;
  supportsCumulative: boolean;
  eventDate: string;
  definition: string;
  measurement: string;
  owner: string;
  dataSource: string;
  updateNotes: string;
  initiatives: InitiativeItem[];
  tasks: TaskItem[];
  lenses: Lens[];
  lensLabels: string[];
  positionsByLens: LensPositions;
  collapsedInLenses: Lens[];
  status?: Status;
  isRollup: boolean;
  metricsEditable: boolean;
  metricNote?: string;
  statusNote?: string;
  rollupWarning?: string;
  childCount: number;
  childrenCollapsed: boolean;
  onLabelChange: (id: string, value: string) => void;
  onMetricChange: (
    id: string,
    field: "target" | "actual" | "unit",
    value: string
  ) => void;
  onConfigChange: (
    id: string,
    field: "scope" | "performanceDirection" | "supportsCumulative",
    value: Scope | PerformanceDirection | boolean
  ) => void;
  onDetailChange: (id: string, field: DetailFieldKey, value: string) => void;
  onToggleRollup: (id: string, checked: boolean) => void;
  onAddChild: (id: string) => void;
  onToggleChildren: (id: string) => void;
};

const noopLabelChange = (_id: string, _value: string) => {};
const noopMetricChange = (
  _id: string,
  _field: "target" | "actual" | "unit",
  _value: string
) => {};
const noopConfigChange = (
  _id: string,
  _field: "scope" | "performanceDirection" | "supportsCumulative",
  _value: Scope | PerformanceDirection | boolean
) => {};
const noopDetailChange = (_id: string, _field: DetailFieldKey, _value: string) => {};
const noopToggleRollup = (_id: string, _checked: boolean) => {};
const noopAddChild = (_id: string) => {};
const noopToggleChildren = (_id: string) => {};

const initialCampuses: string[] = ["North", "South", "East"];
const initialDepartments: string[] = ["Finance", "Operations", "Communications"];
const initialOwners: string[] = [
  "Campus Pastors and Central Analytics",
  "Student Ministry Directors",
  "Kids Ministry Directors",
  "Executive Pastor of Operations",
];
const scopeOptions: Scope[] = ["campus", "global"];
const performanceDirectionOptions: PerformanceDirection[] = [
  "higher_is_better",
  "lower_is_better",
];
const scopeTypeOptions: ScopeType[] = [
  "organization",
  "campus",
  "ministry",
  "department",
  "service",
];
const initialLenses: Lens[] = ["org-chart-org", "discipleship-org", "operations-org"];
const defaultCampus: Campus = "North";
const initialReferenceDate = new Date(2026, 2, 23);
const defaultLens = initialLenses[0];
const initialLensConfigs: LensConfigs = {
  "org-chart-org": {
    name: "Org Chart",
    scopeType: "organization",
    scopeId: "entire-organization",
    description:
      "Tracks KPIs tied to organizational leadership, ministry oversight, and team accountability.",
  },
  "discipleship-org": {
    name: "Discipleship",
    scopeType: "organization",
    scopeId: "entire-organization",
    description:
      "Focuses on spiritual formation, ministry environments, and growth pathways across the church.",
  },
  "operations-org": {
    name: "Operations",
    scopeType: "organization",
    scopeId: "entire-organization",
    description:
      "Covers execution, finance, systems, and operational health needed to support ministry outcomes.",
  },
};
const workStatusOptions: WorkStatus[] = ["not-started", "in-progress", "done"];
const defaultEnterpriseName = "Default Enterprise";
const KPI_NODE_WIDTH = 220;
const KPI_NODE_HEIGHT = 235;
const KPI_HORIZONTAL_GAP = 280;
const KPI_VERTICAL_GAP = 230;
const KPI_NODE_MARGIN = 32;

const statusColor = (status?: Status) => {
  switch (status) {
    case "green_strong":
      return "#15803d";
    case "green":
      return "#22c55e";
    case "yellow":
      return "#eab308";
    case "red":
      return "#ef4444";
    default:
      return "#9ca3af";
  }
};

const getDirectScore = (
  target?: number,
  actual?: number,
  performanceDirection: PerformanceDirection = "higher_is_better"
): number | null => {
  if (
    typeof target !== "number" ||
    typeof actual !== "number" ||
    Number.isNaN(target) ||
    Number.isNaN(actual) ||
    target <= 0
  ) {
    return null;
  }

  if (performanceDirection === "lower_is_better") {
    if (actual === 0) return Number.POSITIVE_INFINITY;
    return target / actual;
  }

  return actual / target;
};

const getStatusFromScore = (score: number | null): Status => {
  if (score === null) return "neutral";
  if (score >= 1.2) return "green_strong";
  if (score >= 1) return "green";
  if (score >= 0.8) return "yellow";
  return "red";
};

const normalizeInfluence = (edge: Edge): number | null => {
  const raw = edge.data?.influence;
  if (typeof raw !== "number" || Number.isNaN(raw) || raw <= 0) return null;
  return raw / 100;
};

const rectanglesOverlap = (
  left: { x: number; y: number; width: number; height: number },
  right: { x: number; y: number; width: number; height: number }
) =>
  left.x < right.x + right.width &&
  left.x + left.width > right.x &&
  left.y < right.y + right.height &&
  left.y + left.height > right.y;

const padTwo = (value: number) => String(value).padStart(2, "0");

const getIsoWeekParts = (date: Date) => {
  const utcDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  const day = utcDate.getUTCDay() || 7;
  utcDate.setUTCDate(utcDate.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utcDate.getUTCFullYear(), 0, 1));
  const week = Math.ceil(
    (((utcDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7
  );

  return { year: utcDate.getUTCFullYear(), week };
};

const getPeriodKey = (date: Date, frequency: Frequency) => {
  if (frequency === "weekly") {
    const iso = getIsoWeekParts(date);
    return `${iso.year}-W${padTwo(iso.week)}`;
  }

  if (frequency === "monthly") {
    return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}`;
  }

  return `${date.getFullYear()}`;
};

const getScopeKey = (scope: Scope, campus: Campus) => {
  if (scope === "global") return "global";
  return campus;
};

const getMetricKey = (
  date: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus
) => `${getPeriodKey(date, frequency)}::${getScopeKey(scope, campus)}`;

const isDateInFuture = (referenceDate: Date, eventDate: string) => {
  if (!eventDate) return false;
  const parsedEventDate = new Date(`${eventDate}T00:00:00`);
  if (Number.isNaN(parsedEventDate.getTime())) return false;
  return referenceDate < parsedEventDate;
};

const getDefaultScopeId = (scopeType: ScopeType) => {
  switch (scopeType) {
    case "organization":
      return "entire-organization";
    case "ministry":
      return "general-ministry";
    case "department":
      return "general-department";
    case "service":
      return "general-service";
    default:
      return "";
  }
};

const getValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus,
  cumulative = false
): number | undefined => {
  const periodKey = getPeriodKey(referenceDate, frequency);
  const filteredEntries = Object.entries(history).filter(([key]) => {
    const [entryPeriodKey, entryScopeKey] = key.split("::");
    if (!entryPeriodKey || !entryScopeKey || entryPeriodKey > periodKey) return false;

    if (scope === "global") {
      return entryScopeKey === "global";
    }

    if (campus === "All") {
      return entryScopeKey !== "global";
    }

    return entryScopeKey === campus;
  });

  if (cumulative) {
    const values = filteredEntries
      .map(([, value]) => value)
      .filter((value): value is number => typeof value === "number");
    if (values.length === 0) return undefined;
    return values.reduce((total, value) => total + value, 0);
  }

  if (scope === "global") {
    return history[`${periodKey}::global`];
  }

  if (campus === "All") {
    const values = Object.entries(history)
      .filter(([key]) => key.startsWith(`${periodKey}::`) && key !== `${periodKey}::global`)
      .map(([, value]) => value)
      .filter((value): value is number => typeof value === "number");

    if (values.length === 0) return undefined;
    return values.reduce((sum, value) => sum + value, 0);
  }

  return history[`${periodKey}::${campus}`];
};

const setValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus,
  value: number | undefined
): MetricHistory => {
  if (scope === "campus" && campus === "All") return history;

  const key = getMetricKey(referenceDate, frequency, scope, campus);
  return {
    ...history,
    [key]: value,
  };
};

const shiftReferenceDate = (date: Date, direction: "previous" | "next") => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + (direction === "next" ? 7 : -7));
  return nextDate;
};

const formatReferenceDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const formatReferenceSummary = (date: Date) => {
  const iso = getIsoWeekParts(date);
  return `Wk ${iso.week}, ${iso.year} | ${date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  })}`;
};

const toTitleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const formatEnumLabel = (value: string) =>
  value
    .split("_")
    .map((part) => toTitleCase(part))
    .join(" ");

const createTask = (overrides: Partial<TaskItem> = {}): TaskItem => ({
  id: uuidv4(),
  title: "New task",
  owner: "",
  dueDate: "",
  status: "not-started",
  notes: "",
  ...overrides,
});

const createInitiative = (
  overrides: Partial<Omit<InitiativeItem, "tasks">> & { tasks?: TaskItem[] } = {}
): InitiativeItem => ({
  id: uuidv4(),
  title: "New initiative",
  owner: "",
  dueDate: "",
  status: "not-started",
  summary: "",
  tasks: [],
  ...overrides,
});

const stripNodeDataForPersistence = (data: KpiNodeData) => ({
  label: data.label,
  targetHistory: data.targetHistory,
  actualHistory: data.actualHistory,
  unit: data.unit ?? "",
  frequency: data.frequency,
  scope: data.scope,
  performanceDirection: data.performanceDirection,
  supportsCumulative: data.supportsCumulative,
  eventDate: data.eventDate,
  definition: data.definition,
  measurement: data.measurement,
  owner: data.owner,
  dataSource: data.dataSource,
  updateNotes: data.updateNotes,
  initiatives: data.initiatives,
  tasks: data.tasks,
  lenses: data.lenses,
  lensLabels: data.lensLabels,
  positionsByLens: data.positionsByLens,
  collapsedInLenses: data.collapsedInLenses,
  isRollup: data.isRollup,
});

const hydrateNodeDataFromPersistence = (raw: Partial<KpiNodeData>): KpiNodeData => ({
  label: raw.label ?? "New KPI",
  target: undefined,
  targetHistory: raw.targetHistory ?? {},
  actual: undefined,
  actualHistory: raw.actualHistory ?? {},
  unit: raw.unit ?? "",
  frequency: raw.frequency ?? "weekly",
  scope: raw.scope ?? "campus",
  performanceDirection: raw.performanceDirection ?? "higher_is_better",
  supportsCumulative: raw.supportsCumulative ?? false,
  eventDate: raw.eventDate ?? "",
  definition: raw.definition ?? "Describe what this KPI is intended to measure.",
  measurement: raw.measurement ?? "Describe exactly how this KPI is counted or calculated.",
  owner: raw.owner ?? "Assign an owner",
  dataSource: raw.dataSource ?? "Document the source system or report",
  updateNotes: raw.updateNotes ?? "Add any timing, validation, or reporting notes here.",
  initiatives: raw.initiatives ?? [],
  tasks: raw.tasks ?? [],
  lenses: raw.lenses ?? [],
  lensLabels: raw.lensLabels ?? [],
  positionsByLens: raw.positionsByLens ?? {},
  collapsedInLenses: raw.collapsedInLenses ?? [],
  status: "neutral",
  isRollup: raw.isRollup ?? false,
  metricsEditable: true,
  metricNote: undefined,
  statusNote: undefined,
  rollupWarning: undefined,
  childCount: 0,
  childrenCollapsed: false,
  onLabelChange: noopLabelChange,
  onMetricChange: noopMetricChange,
  onConfigChange: noopConfigChange,
  onDetailChange: noopDetailChange,
  onToggleRollup: noopToggleRollup,
  onAddChild: noopAddChild,
  onToggleChildren: noopToggleChildren,
});

const stopCanvasDrag = (e: React.MouseEvent | React.PointerEvent) => {
  e.stopPropagation();
};

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    target.isContentEditable
  );
};

function DetailField({
  label,
  value,
  onChange,
  multiline = false,
  readOnly = false,
  onBlur,
  onKeyDown,
  listId,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  readOnly?: boolean;
  onBlur?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  listId?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: "#6b7280",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {multiline ? (
        <textarea
          className="nodrag nopan"
          value={value}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            minHeight: 96,
            resize: "vertical",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 14,
            color: "#111827",
            lineHeight: 1.5,
            background: readOnly ? "#f9fafb" : "#fff",
          }}
        />
      ) : (
        <input
          className="nodrag nopan"
          value={value}
          list={listId}
          readOnly={readOnly}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 14,
            color: "#111827",
            lineHeight: 1.5,
            background: readOnly ? "#f9fafb" : "#fff",
          }}
        />
      )}
    </div>
  );
}

function WorkItemField({
  value,
  onChange,
  placeholder,
  listId,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  listId?: string;
}) {
  return (
    <input
      className="nodrag nopan"
      value={value}
      list={listId}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onMouseDown={stopCanvasDrag}
      onPointerDown={stopCanvasDrag}
      style={{
        width: "100%",
        boxSizing: "border-box",
        border: "1px solid #d1d5db",
        borderRadius: 10,
        padding: "8px 10px",
        fontSize: 13,
        color: "#111827",
        background: "#fff",
      }}
    />
  );
}

function StringListEditor({
  title,
  items,
  onChange,
}: {
  title: string;
  items: string[];
  onChange: (nextItems: string[]) => void;
}) {
  const normalizeItems = (nextItems: string[]) =>
    nextItems.map((item) => item.trim()).filter((item) => item.length > 0);

  return (
    <div
      style={{
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div
        style={{
          fontSize: 16,
          fontWeight: 800,
          color: "#111827",
          marginBottom: 10,
        }}
      >
        {title}
      </div>

      <div
        style={{
          display: "grid",
          gap: 8,
        }}
      >
        {items.map((item, index) => (
          <div
            key={`${title}-${index}`}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 8,
            }}
          >
            <input
              className="nodrag nopan"
              value={item}
              onChange={(e) =>
                onChange(items.map((current, currentIndex) =>
                  currentIndex === index ? e.target.value : current
                ))
              }
              onBlur={() => onChange(normalizeItems(items))}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: "1px solid #d1d5db",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 13,
                color: "#111827",
                background: "#fff",
              }}
            />
            <button
              type="button"
              className="nodrag nopan"
              onClick={() => onChange(items.filter((_, currentIndex) => currentIndex !== index))}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "1px solid #dc2626",
                borderRadius: 10,
                padding: "8px 10px",
                fontSize: 13,
                fontWeight: 700,
                background: "#fff5f5",
                color: "#b91c1c",
                cursor: "pointer",
              }}
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        className="nodrag nopan"
        onClick={() => onChange([...items, ""])}
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          marginTop: 10,
          border: "1px solid #d1d5db",
          borderRadius: 10,
          padding: "8px 10px",
          fontSize: 13,
          fontWeight: 700,
          background: "#fff",
          color: "#111827",
          cursor: "pointer",
        }}
      >
        + Add
      </button>
    </div>
  );
}

function KpiNode({ id, data }: { id: string; data: KpiNodeData }) {
  const metricsDisabled = data.isRollup || !data.metricsEditable;

  return (
    <div
      style={{
        background: statusColor(data.status),
        borderRadius: 10,
        padding: 8,
        width: 220,
        boxSizing: "border-box",
        boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 10,
          padding: 10,
          border: "2px solid #e5e7eb",
          boxSizing: "border-box",
        }}
      >
        <input
          className="nodrag nopan"
          value={data.label}
          onChange={(e) => data.onLabelChange(id, e.target.value)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            boxSizing: "border-box",
            fontSize: 15,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 8,
            border: "none",
            outline: "none",
            background: "transparent",
          }}
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            marginBottom: 8,
          }}
        >
          <div
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 12,
              background: "#f9fafb",
              color: "#111827",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
            }}
          >
            {toTitleCase(data.frequency)}
          </div>
          <select
            className="nodrag nopan"
            value={data.scope}
            onChange={(e) => data.onConfigChange(id, "scope", e.target.value as Scope)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 12,
              background: "#fff",
              color: "#111827",
            }}
          >
            {scopeOptions.map((option) => (
              <option key={option} value={option}>
                {toTitleCase(option)}
              </option>
            ))}
          </select>
        </div>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 8,
            fontSize: 12,
            fontWeight: 600,
            color: "#374151",
          }}
        >
          <input
            className="nodrag nopan"
            type="checkbox"
            checked={data.isRollup}
            onChange={(e) => data.onToggleRollup(id, e.target.checked)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
          />
          Roll up from child KPIs
        </label>

        <div
          style={{
            fontSize: 12,
            color: "#6b7280",
            marginBottom: 8,
            lineHeight: 1.4,
          }}
        >
          Lenses: {data.lensLabels.join(", ")}
        </div>

        {data.metricNote ? (
          <div
            style={{
              fontSize: 12,
              color: "#6b7280",
              marginBottom: 10,
              lineHeight: 1.4,
            }}
          >
            {data.metricNote}
          </div>
        ) : null}

        {data.statusNote ? (
          <div
            style={{
              fontSize: 12,
              color: "#374151",
              marginBottom: 8,
              lineHeight: 1.4,
              fontWeight: 700,
            }}
          >
            {data.statusNote}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
              gap: 8,
              marginBottom: 6,
              opacity: metricsDisabled ? 0.5 : 1,
            }}
          >
          <input
            className="nodrag nopan"
            type="number"
            placeholder="Target"
            disabled={metricsDisabled}
            value={data.target ?? ""}
            onChange={(e) => data.onMetricChange(id, "target", e.target.value)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 12,
              background: metricsDisabled ? "#f3f4f6" : "#fff",
              color: "#111827",
            }}
          />
          <input
            className="nodrag nopan"
            type="number"
            placeholder="Actual"
            disabled={metricsDisabled}
            value={data.actual ?? ""}
            onChange={(e) => data.onMetricChange(id, "actual", e.target.value)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 12,
              background: metricsDisabled ? "#f3f4f6" : "#fff",
              color: "#111827",
            }}
          />
        </div>

        <input
          className="nodrag nopan"
          placeholder="Unit"
          value={data.unit ?? ""}
          onChange={(e) => data.onMetricChange(id, "unit", e.target.value)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            padding: "6px 8px",
            fontSize: 12,
            marginBottom: 8,
            background: "#fff",
            color: "#111827",
          }}
        />

        {data.isRollup ? (
          <div
            style={{
              fontSize: 12,
              color: data.rollupWarning ? "#b91c1c" : "#4b5563",
              marginBottom: 8,
              lineHeight: 1.4,
              fontWeight: data.rollupWarning ? 700 : 500,
            }}
          >
            {data.rollupWarning || "Inherited from child KPIs"}
          </div>
        ) : (
          <div
            style={{
              fontSize: 12,
              color: "#4b5563",
              marginBottom: 8,
              lineHeight: 1.4,
            }}
          >
            Actual: {data.actual ?? "-"} {data.unit ?? ""} | Target: {data.target ?? "-"}{" "}
            {data.unit ?? ""}
          </div>
        )}

        <button
          className="nodrag nopan"
          onClick={() => data.onAddChild(id)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            background: "#e5e7eb",
            border: "none",
            borderRadius: 8,
            padding: "6px 8px",
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            width: "100%",
          }}
        >
          + KPI
        </button>

        {data.childCount > 0 ? (
          <button
            className="nodrag nopan"
            onClick={() => data.onToggleChildren(id)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "6px 8px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              width: "100%",
              marginTop: 6,
            }}
          >
            {data.childrenCollapsed ? "Show" : "Hide"} {data.childCount}{" "}
            {data.childCount === 1 ? "Child" : "Children"}
          </button>
        ) : null}
      </div>

      <Handle type="target" position={Position.Bottom} />
      <Handle type="source" position={Position.Top} />
    </div>
  );
}

const nodeTypes = {
  kpi: KpiNode,
};

function makeEdge(
  id: string,
  source: string,
  target: string,
  influence?: number
): Edge {
  return {
    id,
    source,
    target,
    type: "default",
    label: typeof influence === "number" ? `${influence}%` : "Set %",
    data: { influence },
    interactionWidth: 40,
    style: { stroke: "#ffffff", strokeWidth: 2 },
    labelStyle: { fill: "#ffffff", fontWeight: 700, fontSize: 16 },
    labelBgStyle: { fill: "#000000", fillOpacity: 1 },
    labelBgPadding: [6, 4],
    labelBgBorderRadius: 4,
    markerEnd: { type: MarkerType.ArrowClosed, color: "#ffffff" },
  };
}

const makeSeedNode = (
  id: string,
  positionsByLens: LensPositions,
  seed: {
    label: string;
    targetHistory: MetricHistory;
    actualHistory: MetricHistory;
    unit: string;
    frequency: Frequency;
    scope: Scope;
    performanceDirection?: PerformanceDirection;
    supportsCumulative?: boolean;
    eventDate?: string;
    definition: string;
    measurement: string;
    owner: string;
    dataSource: string;
    updateNotes: string;
    initiatives: InitiativeItem[];
    tasks: TaskItem[];
    lenses: Lens[];
    isRollup: boolean;
  },
  referenceDate: Date,
  campus: Campus,
  activeLens: Lens
): Node<KpiNodeData> => ({
  id,
  type: "kpi",
  position: positionsByLens[activeLens] ?? { x: 0, y: 0 },
  data: {
    label: seed.label,
    target: getValueForContext(
      seed.targetHistory,
      referenceDate,
      seed.frequency,
      seed.scope,
      campus
    ),
    targetHistory: seed.targetHistory,
    actual: getValueForContext(
      seed.actualHistory,
      referenceDate,
      seed.frequency,
      seed.scope,
      campus
    ),
    actualHistory: seed.actualHistory,
    unit: seed.unit,
    frequency: seed.frequency,
    scope: seed.scope,
    performanceDirection: seed.performanceDirection ?? "higher_is_better",
    supportsCumulative: seed.supportsCumulative ?? false,
    eventDate: seed.eventDate ?? "",
    definition: seed.definition,
    measurement: seed.measurement,
    owner: seed.owner,
    dataSource: seed.dataSource,
    updateNotes: seed.updateNotes,
    initiatives: seed.initiatives,
    tasks: seed.tasks,
    lenses: seed.lenses,
    lensLabels: seed.lenses,
    positionsByLens,
    collapsedInLenses: [],
    status: "neutral",
    isRollup: seed.isRollup,
    metricsEditable: true,
    metricNote: undefined,
    statusNote: undefined,
    rollupWarning: undefined,
    childCount: 0,
    childrenCollapsed: false,
    onLabelChange: noopLabelChange,
    onMetricChange: noopMetricChange,
    onConfigChange: noopConfigChange,
    onDetailChange: noopDetailChange,
    onToggleRollup: noopToggleRollup,
    onAddChild: noopAddChild,
    onToggleChildren: noopToggleChildren,
  },
});

export default function StrategyMapEditor() {
  const allKpisFilterValue = "__all__";
  const unattachedKpisFilterValue = "__unattached__";

  const [availableLenses, setAvailableLenses] = React.useState<Lens[]>(initialLenses);
  const [lensConfigs, setLensConfigs] = React.useState<LensConfigs>(initialLensConfigs);
  const [activeLens, setActiveLens] = React.useState<Lens>(defaultLens);
  const [referenceDate, setReferenceDate] = React.useState(initialReferenceDate);
  const [isCumulativeView, setIsCumulativeView] = React.useState(false);
  const [campus, setCampus] = React.useState<Campus>(defaultCampus);
  const [campusList, setCampusList] = React.useState<string[]>(initialCampuses);
  const [departmentList, setDepartmentList] = React.useState<string[]>(initialDepartments);
  const [ownerList, setOwnerList] = React.useState<string[]>(initialOwners);
  const [enterprises, setEnterprises] = React.useState<EnterpriseSummary[]>([]);
  const [activeEnterpriseId, setActiveEnterpriseId] = React.useState<number | null>(null);
  const [isEnterpriseLoading, setIsEnterpriseLoading] = React.useState(true);
  const [activeKpiId, setActiveKpiId] = React.useState<string | null>(null);
  const [activeLensDetailId, setActiveLensDetailId] = React.useState<Lens | null>(null);
  const [lensNameDrafts, setLensNameDrafts] = React.useState<Record<string, string>>(() =>
    Object.fromEntries(initialLenses.map((lens) => [lens, initialLensConfigs[lens].name]))
  );
  const [isCreateLensOpen, setIsCreateLensOpen] = React.useState(false);
  const [newLensForm, setNewLensForm] = React.useState<{
    name: string;
    scopeType: ScopeType;
    scopeId: string;
    description: string;
  }>({
    name: "",
    scopeType: "organization",
    scopeId: "",
    description: "",
  });
  const [kpiLibraryFilter, setKpiLibraryFilter] = React.useState(allKpisFilterValue);
  const [isLensLibraryCollapsed, setIsLensLibraryCollapsed] = React.useState(true);
  const [isReferenceLibraryCollapsed, setIsReferenceLibraryCollapsed] = React.useState(true);
  const [isLibraryCollapsed, setIsLibraryCollapsed] = React.useState(true);
  const [panelOffsets, setPanelOffsets] = React.useState<
    Record<FloatingPanelKey, { x: number; y: number }>
  >({
    lens: { x: 0, y: 0 },
    reference: { x: 0, y: 0 },
    kpi: { x: 0, y: 0 },
  });
  const [draggingPanel, setDraggingPanel] = React.useState<FloatingPanelKey | null>(null);
  const [reactFlowInstance, setReactFlowInstance] =
    React.useState<ReactFlowInstance | null>(null);
  const panelDragRef = React.useRef<{
    panel: FloatingPanelKey;
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const hasLoadedEnterpriseRef = React.useRef(false);
  const applyWorkspaceRef = React.useRef<(workspace?: Record<string, unknown>) => void>(() => {});

  const defaultNodes = React.useMemo<Node<KpiNodeData>[]>(() => [
    makeSeedNode(
      "attendance",
      {
        "org-chart-org": { x: 420, y: 60 },
        "operations-org": { x: 360, y: 80 },
      },
      {
        label: "Attendance",
        targetHistory: {
          "2026-W13::North": 260,
          "2026-W13::South": 240,
          "2026-W14::North": 265,
          "2026-W14::South": 245,
        },
        actualHistory: {
          "2026-W13::North": 210,
          "2026-W13::South": 220,
          "2026-W14::North": 232,
          "2026-W14::South": 214,
        },
        unit: "people",
        frequency: "weekly",
        scope: "campus",
        definition:
          "Total in-person weekend attendance across all scheduled worship gatherings for the selected reporting period.",
        measurement:
          "Count unique physical attendees checked in or manually counted during each weekend service, then total them for the campus and period.",
        owner: "Campus Pastors and Central Analytics",
        dataSource: "Planning Center check-ins plus service headcount sheets",
        updateNotes:
          "Published every Tuesday after weekend reconciliation. Duplicates and late entries are corrected before finalizing the week.",
        initiatives: [
          createInitiative({
            title: "Spring invite push",
            owner: "Campus Pastors",
            dueDate: "2026-04-15",
            status: "in-progress",
            summary: "Coordinate an invite campaign to support Easter attendance growth.",
            tasks: [
              createTask({
                title: "Finalize invite card design",
                owner: "Communications",
                dueDate: "2026-03-28",
                status: "done",
              }),
              createTask({
                title: "Recruit volunteer follow-up team",
                owner: "Connections Director",
                dueDate: "2026-04-02",
                status: "in-progress",
              }),
            ],
          }),
        ],
        tasks: [
          createTask({
            title: "Review weekend attendance exceptions",
            owner: "Analytics",
            dueDate: "2026-03-24",
            status: "in-progress",
          }),
        ],
        lenses: ["org-chart-org", "operations-org"],
        isRollup: true,
      },
      initialReferenceDate,
      defaultCampus,
      defaultLens
    ),
    makeSeedNode(
      "youth",
      {
        "org-chart-org": { x: 70, y: 340 },
        "discipleship-org": { x: 120, y: 160 },
      },
      {
        label: "Youth",
        targetHistory: {
          "2026-W13::North": 55,
          "2026-W13::South": 45,
          "2026-W14::North": 58,
          "2026-W14::South": 46,
        },
        actualHistory: {
          "2026-W13::North": 62,
          "2026-W13::South": 58,
          "2026-W14::North": 64,
          "2026-W14::South": 55,
        },
        unit: "people",
        frequency: "weekly",
        scope: "campus",
        definition:
          "Students from middle school through high school who participated in the campus youth environment during the selected week.",
        measurement:
          "Count students checked into youth programming once per week per campus, regardless of how many sessions they attended.",
        owner: "Student Ministry Directors",
        dataSource: "Student ministry check-ins and volunteer reconciliation notes",
        updateNotes:
          "Reviewed weekly by the campus youth lead. Any special event attendance is included only if it replaces the regular gathering.",
        initiatives: [
          createInitiative({
            title: "Wednesday night relaunch",
            owner: "Student Ministry Directors",
            dueDate: "2026-05-01",
            status: "not-started",
            summary: "Improve student retention and weekly participation with a refreshed format.",
          }),
        ],
        tasks: [
          createTask({
            title: "Confirm volunteer coverage",
            owner: "Youth Admin",
            dueDate: "2026-03-27",
            status: "not-started",
          }),
        ],
        lenses: ["org-chart-org", "discipleship-org"],
        isRollup: false,
      },
      initialReferenceDate,
      defaultCampus,
      defaultLens
    ),
    makeSeedNode(
      "children",
      {
        "org-chart-org": { x: 770, y: 340 },
        "discipleship-org": { x: 490, y: 170 },
      },
      {
        label: "Children Check-ins",
        targetHistory: {
          "2026-03::North": 180,
          "2026-03::South": 150,
          "2026-04::North": 185,
          "2026-04::South": 155,
        },
        actualHistory: {
          "2026-03::North": 176,
          "2026-03::South": 147,
          "2026-04::North": 191,
          "2026-04::South": 149,
        },
        unit: "people",
        frequency: "monthly",
        scope: "campus",
        definition:
          "Average monthly child check-ins for ministry environments serving nursery through elementary ages.",
        measurement:
          "Total completed child check-ins for the month divided by the number of weekend ministry opportunities in that month.",
        owner: "Kids Ministry Directors",
        dataSource: "Kids check-in system and monthly ministry calendar",
        updateNotes:
          "Locked after month-end close. This KPI intentionally smooths week-to-week volatility by reporting monthly averages.",
        initiatives: [],
        tasks: [],
        lenses: ["org-chart-org", "discipleship-org"],
        isRollup: false,
      },
      initialReferenceDate,
      defaultCampus,
      defaultLens
    ),
    makeSeedNode(
      "main-service",
      {
        "org-chart-org": { x: 420, y: 620 },
        "operations-org": { x: 710, y: 190 },
      },
      {
        label: "Annual Giving",
        targetHistory: {
          "2026::global": 520000,
        },
        actualHistory: {
          "2026::global": 486500,
        },
        unit: "USD",
        frequency: "yearly",
        scope: "global",
        supportsCumulative: true,
        definition:
          "Year-to-date giving toward the annual ministry budget across the whole organization.",
        measurement:
          "Sum all posted donations, excluding non-operating pass-through funds, and compare against the annual finance target.",
        owner: "Executive Pastor of Operations",
        dataSource: "Finance ledger and contribution reporting",
        updateNotes:
          "Updated after finance close. This KPI is organization-wide and is not split by campus.",
        initiatives: [
          createInitiative({
            title: "Mid-year giving review",
            owner: "Finance Team",
            dueDate: "2026-06-30",
            status: "not-started",
            summary: "Assess run-rate versus budget and identify corrective actions.",
          }),
        ],
        tasks: [
          createTask({
            title: "Prepare giving variance summary",
            owner: "Finance Analyst",
            dueDate: "2026-04-05",
            status: "not-started",
          }),
        ],
        lenses: ["org-chart-org", "operations-org"],
        isRollup: false,
      },
      initialReferenceDate,
      defaultCampus,
      defaultLens
    ),
  ], []);

  const defaultEdges = React.useMemo<Edge[]>(() => [
    makeEdge("e-youth-attendance", "youth", "attendance", 30),
    makeEdge("e-children-attendance", "children", "attendance", 20),
    makeEdge("e-mainservice-attendance", "main-service", "attendance", 50),
  ], []);

  const [allNodes, setAllNodes] = React.useState<Node<KpiNodeData>[]>(defaultNodes);
  const [allEdges, setAllEdges] = React.useState<Edge[]>(defaultEdges);

  const syncNodesToContext = React.useCallback(
    (
      currentNodes: Node<KpiNodeData>[],
      nextReferenceDate: Date,
      nextCampus: Campus,
      nextLens: Lens
    ) =>
      currentNodes.map((node) => {
        const visibleInLens = node.data.lenses.includes(nextLens);
        const cumulativeModeActive = isCumulativeView && node.data.supportsCumulative;
        const eventUpcoming = isDateInFuture(nextReferenceDate, node.data.eventDate);
        const metricsEditable =
          !node.data.isRollup &&
          visibleInLens &&
          (node.data.scope === "global" || nextCampus !== "All");
        const metricNote = !visibleInLens
          ? `Available in: ${node.data.lenses.join(", ")}`
          : node.data.scope === "global"
            ? "Global KPI. Campus filter does not change this value."
            : nextCampus === "All"
              ? "Viewing all campuses combined. Switch to a campus to edit values."
              : undefined;

        return {
          ...node,
          position: node.data.positionsByLens[nextLens] ?? node.position,
          data: {
            ...node.data,
            target: getValueForContext(
              node.data.targetHistory,
              nextReferenceDate,
              node.data.frequency,
              node.data.scope,
              nextCampus,
              cumulativeModeActive
            ),
            actual: getValueForContext(
              node.data.actualHistory,
              nextReferenceDate,
              node.data.frequency,
              node.data.scope,
              nextCampus,
              cumulativeModeActive
            ),
            metricsEditable,
            metricNote,
            statusNote: eventUpcoming
              ? "Upcoming"
              : cumulativeModeActive
                ? "Cumulative view"
                : undefined,
          },
        };
      }),
    [isCumulativeView]
  );

  const recalculateNodes = React.useCallback(
    (currentNodes: Node<KpiNodeData>[], currentEdges: Edge[]) => {
      const nodeMap = new Map(currentNodes.map((node) => [node.id, node]));
      const incomingMap = new Map<string, Edge[]>();

      currentEdges.forEach((edge) => {
        if (!incomingMap.has(edge.target)) incomingMap.set(edge.target, []);
        incomingMap.get(edge.target)!.push(edge);
      });

      const computeScore = (
        nodeId: string,
        visiting = new Set<string>()
      ): { score: number | null; warning?: string } => {
        if (visiting.has(nodeId)) {
          return { score: null, warning: "Circular dependency detected" };
        }

        const node = nodeMap.get(nodeId);
        if (!node) return { score: null, warning: "Missing node" };

        const data = node.data;
        const incomingEdges = incomingMap.get(nodeId) || [];

        if (!data.isRollup) {
          if (data.statusNote === "Upcoming") {
            return { score: null };
          }
          return {
            score: getDirectScore(
              data.target,
              data.actual,
              data.performanceDirection
            ),
          };
        }

        if (incomingEdges.length === 0) {
          return { score: null, warning: "Rollup KPI has no child KPIs" };
        }

        let configuredWeightTotal = 0;
        let weightedSum = 0;
        let validWeightTotal = 0;
        let missingWeight = false;
        let missingChildScore = false;
        let hasUpcomingChildren = false;
        let hasChildWarning = false;

        for (const edge of incomingEdges) {
          const childNode = nodeMap.get(edge.source);
          if (childNode?.data.statusNote === "Upcoming") {
            hasUpcomingChildren = true;
            continue;
          }

          const influence = normalizeInfluence(edge);

          if (influence === null) {
            missingWeight = true;
            continue;
          }

          configuredWeightTotal += influence;

          const nextVisiting = new Set(visiting);
          nextVisiting.add(nodeId);
          const childResult = computeScore(edge.source, nextVisiting);

          if (childResult.warning) {
            hasChildWarning = true;
          }

          if (childResult.score !== null) {
            weightedSum += childResult.score * influence;
            validWeightTotal += influence;
          } else {
            missingChildScore = true;
          }
        }

        if (missingWeight) {
          return {
            score: null,
            warning: "One or more child relationships are missing influence %",
          };
        }

        if (!hasUpcomingChildren && Math.abs(configuredWeightTotal - 1) > 0.001) {
          return {
            score: null,
            warning: `Influence must total 100% (currently ${Math.round(
              configuredWeightTotal * 100
            )}%)`,
          };
        }

        if (hasChildWarning) {
          return {
            score: null,
            warning: "One or more child KPIs need attention before this rollup can score",
          };
        }

        if (missingChildScore) {
          return {
            score: null,
            warning: "One or more child KPIs are still neutral",
          };
        }

        if (validWeightTotal === 0) {
          return {
            score: null,
            warning: hasUpcomingChildren
              ? "Upcoming"
              : "Child KPIs have no valid weighted performance",
          };
        }

        return {
          score: weightedSum / validWeightTotal,
        };
      };

      return currentNodes.map((node) => {
        const result = computeScore(node.id);
        return {
          ...node,
          data: {
            ...node.data,
            status: getStatusFromScore(result.score),
            rollupWarning: node.data.isRollup ? result.warning : undefined,
          },
        };
      });
    },
    []
  );

  const applyContextAndRecalculate: (
    nextNodes: Node<KpiNodeData>[],
    nextEdges: Edge[],
    nextReferenceDate?: Date,
    nextCampus?: Campus,
    nextLens?: Lens
  ) => Node<KpiNodeData>[] = React.useCallback(
    (
      nextNodes: Node<KpiNodeData>[],
      nextEdges: Edge[],
      nextReferenceDate = referenceDate,
      nextCampus = campus,
      nextLens = activeLens
    ): Node<KpiNodeData>[] =>
      recalculateNodes(
        wireHandlers(
          syncNodesToContext(nextNodes, nextReferenceDate, nextCampus, nextLens)
        ),
        nextEdges
      ),
    [activeLens, campus, recalculateNodes, referenceDate, syncNodesToContext]
  );

  const updateNodeById = React.useCallback(
    (id: string, updater: (node: Node<KpiNodeData>) => Node<KpiNodeData>) => {
      setAllNodes((previous) =>
        applyContextAndRecalculate(
          previous.map((node) => (node.id === id ? updater(node) : node)),
          allEdges
        )
      );
    },
    [allEdges, applyContextAndRecalculate]
  );

  const onLabelChange = React.useCallback(
    (id: string, value: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          label: value,
        },
      }));
    },
    [updateNodeById]
  );

  const onMetricChange = React.useCallback(
    (id: string, field: "target" | "actual" | "unit", value: string) => {
      updateNodeById(id, (node) => {
        if (field === "unit") {
          return {
            ...node,
            data: {
              ...node.data,
              unit: value,
            },
          };
        }

        const nextValue = value === "" ? undefined : Number(value);
        const historyField = field === "target" ? "targetHistory" : "actualHistory";

        return {
          ...node,
          data: {
            ...node.data,
            [field]: nextValue,
            [historyField]: setValueForContext(
              node.data[historyField],
              referenceDate,
              node.data.frequency,
              node.data.scope,
              campus,
              nextValue
            ),
          },
        };
      });
    },
    [campus, referenceDate, updateNodeById]
  );

  const onConfigChange = React.useCallback(
    (
      id: string,
      field: "scope" | "performanceDirection" | "supportsCumulative",
      value: Scope | PerformanceDirection | boolean
    ) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
        },
      }));
    },
    [updateNodeById]
  );

  const onDetailChange = React.useCallback(
    (id: string, field: DetailFieldKey, value: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
        },
      }));
    },
    [updateNodeById]
  );

  const onToggleRollup = React.useCallback(
    (id: string, checked: boolean) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          isRollup: checked,
        },
      }));
    },
    [updateNodeById]
  );

  const onAddKpiTask = React.useCallback(
    (id: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: [...node.data.tasks, createTask()],
        },
      }));
    },
    [updateNodeById]
  );

  const onTaskChange = React.useCallback(
    (id: string, taskId: string, field: keyof Omit<TaskItem, "id">, value: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: node.data.tasks.map((task) =>
            task.id === taskId ? { ...task, [field]: value } : task
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onDeleteKpiTask = React.useCallback(
    (id: string, taskId: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: node.data.tasks.filter((task) => task.id !== taskId),
        },
      }));
    },
    [updateNodeById]
  );

  const onAddInitiative = React.useCallback(
    (id: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: [...node.data.initiatives, createInitiative()],
        },
      }));
    },
    [updateNodeById]
  );

  const onInitiativeChange = React.useCallback(
    (
      id: string,
      initiativeId: string,
      field: keyof Omit<InitiativeItem, "id" | "tasks">,
      value: string
    ) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === initiativeId
              ? { ...initiative, [field]: value }
              : initiative
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onDeleteInitiative = React.useCallback(
    (id: string, initiativeId: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.filter(
            (initiative) => initiative.id !== initiativeId
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onAddInitiativeTask = React.useCallback(
    (id: string, initiativeId: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === initiativeId
              ? {
                  ...initiative,
                  tasks: [...initiative.tasks, createTask()],
                }
              : initiative
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onInitiativeTaskChange = React.useCallback(
    (
      id: string,
      initiativeId: string,
      taskId: string,
      field: keyof Omit<TaskItem, "id">,
      value: string
    ) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === initiativeId
              ? {
                  ...initiative,
                  tasks: initiative.tasks.map((task) =>
                    task.id === taskId ? { ...task, [field]: value } : task
                  ),
                }
              : initiative
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const onDeleteInitiativeTask = React.useCallback(
    (id: string, initiativeId: string, taskId: string) => {
      updateNodeById(id, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: node.data.initiatives.map((initiative) =>
            initiative.id === initiativeId
              ? {
                  ...initiative,
                  tasks: initiative.tasks.filter((task) => task.id !== taskId),
                }
              : initiative
          ),
        },
      }));
    },
    [updateNodeById]
  );

  const getVisibleNodeIdsForLens = React.useCallback(
    (nodes: Node<KpiNodeData>[], lens: Lens) => {
      const lensNodes = nodes.filter((node) => node.data.lenses.includes(lens));
      const lensEdges = allEdges.filter((edge) => {
        const sourceNode = lensNodes.find((node) => node.id === edge.source);
        const targetNode = lensNodes.find((node) => node.id === edge.target);
        return Boolean(sourceNode && targetNode);
      });

      const childIdsByParent = new Map<string, string[]>();
      const parentIdsByChild = new Map<string, string[]>();

      lensEdges.forEach((edge) => {
        if (!childIdsByParent.has(edge.target)) {
          childIdsByParent.set(edge.target, []);
        }
        childIdsByParent.get(edge.target)!.push(edge.source);

        if (!parentIdsByChild.has(edge.source)) {
          parentIdsByChild.set(edge.source, []);
        }
        parentIdsByChild.get(edge.source)!.push(edge.target);
      });

      const topLevelNodeIds = lensNodes
        .filter((node) => !parentIdsByChild.has(node.id))
        .map((node) => node.id);

      const visibleNodeIds = new Set<string>();
      const queue =
        topLevelNodeIds.length > 0
          ? [...topLevelNodeIds]
          : lensNodes.map((node) => node.id);

      while (queue.length > 0) {
        const nodeId = queue.shift()!;
        if (visibleNodeIds.has(nodeId)) continue;
        visibleNodeIds.add(nodeId);

        const node = lensNodes.find((item) => item.id === nodeId);
        if (!node || node.data.collapsedInLenses.includes(lens)) continue;

        (childIdsByParent.get(nodeId) ?? []).forEach((childId) => {
          if (!visibleNodeIds.has(childId)) {
            queue.push(childId);
          }
        });
      }

      return visibleNodeIds;
    },
    [allEdges]
  );

  const autoLayoutChildren = React.useCallback(
    (nodes: Node<KpiNodeData>[], parentIds: string[]) => {
      if (parentIds.length === 0) return nodes;

      const nextNodes = nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          positionsByLens: { ...node.data.positionsByLens },
          collapsedInLenses: [...node.data.collapsedInLenses],
        },
      }));
      const nodeMap = new Map(nextNodes.map((node) => [node.id, node]));
      const visibleNodeIds = getVisibleNodeIdsForLens(nextNodes, activeLens);
      const activeLensEdges = allEdges.filter(
        (edge) =>
          nodeMap.get(edge.source)?.data.lenses.includes(activeLens) &&
          nodeMap.get(edge.target)?.data.lenses.includes(activeLens)
      );
      const childIdsByParent = new Map<string, string[]>();
      activeLensEdges.forEach((edge) => {
        if (!childIdsByParent.has(edge.target)) {
          childIdsByParent.set(edge.target, []);
        }
        childIdsByParent.get(edge.target)!.push(edge.source);
      });

      const subtreeIds = new Set<string>();
      const collectSubtree = (parentId: string) => {
        (childIdsByParent.get(parentId) ?? []).forEach((childId) => {
          if (subtreeIds.has(childId)) return;
          subtreeIds.add(childId);
          collectSubtree(childId);
        });
      };
      parentIds.forEach(collectSubtree);

      const occupiedRects = nextNodes
        .filter(
          (node) =>
            node.data.lenses.includes(activeLens) &&
            visibleNodeIds.has(node.id) &&
            !subtreeIds.has(node.id)
        )
        .map((node) => {
          const position = node.data.positionsByLens[activeLens] ?? node.position;
          return {
            x: position.x - KPI_NODE_MARGIN,
            y: position.y - KPI_NODE_MARGIN,
            width: KPI_NODE_WIDTH + KPI_NODE_MARGIN * 2,
            height: KPI_NODE_HEIGHT + KPI_NODE_MARGIN * 2,
          };
        });

      const layoutBranch = (parentId: string) => {
        const parentNode = nodeMap.get(parentId);
        if (!parentNode) return;

        const childIds = (childIdsByParent.get(parentId) ?? []).filter((childId) =>
          nodeMap.get(childId)?.data.lenses.includes(activeLens)
        );
        if (childIds.length === 0) return;

        const parentPosition = parentNode.data.positionsByLens[activeLens] ?? parentNode.position;
        const sortedChildIds = [...childIds].sort((leftId, rightId) => {
          const left = nodeMap.get(leftId);
          const right = nodeMap.get(rightId);
          const leftX = left?.data.positionsByLens[activeLens]?.x ?? left?.position.x ?? 0;
          const rightX = right?.data.positionsByLens[activeLens]?.x ?? right?.position.x ?? 0;
          return leftX - rightX;
        });

        const preferredXPositions = sortedChildIds.map(
          (_childId, index) =>
            parentPosition.x +
            (index - (sortedChildIds.length - 1) / 2) * KPI_HORIZONTAL_GAP
        );

        let rowY = parentPosition.y + KPI_VERTICAL_GAP;
        for (let depthOffset = 0; depthOffset < 10; depthOffset += 1) {
          const candidateY = parentPosition.y + KPI_VERTICAL_GAP + depthOffset * KPI_VERTICAL_GAP;
          const candidateRects = preferredXPositions.map((x) => ({
            x: x - KPI_NODE_MARGIN,
            y: candidateY - KPI_NODE_MARGIN,
            width: KPI_NODE_WIDTH + KPI_NODE_MARGIN * 2,
            height: KPI_NODE_HEIGHT + KPI_NODE_MARGIN * 2,
          }));

          const hasCollision = candidateRects.some((candidateRect) =>
            occupiedRects.some((rect) => rectanglesOverlap(rect, candidateRect))
          );

          if (!hasCollision) {
            rowY = candidateY;
            candidateRects.forEach((rect) => occupiedRects.push(rect));
            break;
          }
        }

        sortedChildIds.forEach((childId, index) => {
          const childNode = nodeMap.get(childId);
          if (!childNode) return;

          const nextPosition = {
            x: preferredXPositions[index],
            y: rowY,
          };

          childNode.position = nextPosition;
          childNode.data.positionsByLens[activeLens] = nextPosition;

          if (!childNode.data.collapsedInLenses.includes(activeLens)) {
            layoutBranch(childId);
          }
        });
      };

      parentIds.forEach((parentId) => {
        if (nodeMap.get(parentId)?.data.lenses.includes(activeLens)) {
          layoutBranch(parentId);
        }
      });

      return nextNodes;
    },
    [activeLens, allEdges, getVisibleNodeIdsForLens]
  );

  const onNodesChange = React.useCallback(
    (changes: NodeChange[]) => {
      setAllNodes((previous) => {
        const visible = previous
          .filter((node) => node.data.lenses.includes(activeLens))
          .map((node) => ({
            ...node,
            position: node.data.positionsByLens[activeLens] ?? node.position,
          }));

        const updatedVisible = applyNodeChanges(changes, visible);
        const updatedMap = new Map(updatedVisible.map((node) => [node.id, node]));
        const movedParentIds = changes.reduce<string[]>((ids, change) => {
          if (
            change.type === "position" &&
            "id" in change &&
            "dragging" in change &&
            change.dragging === false
          ) {
            ids.push(change.id);
          }
          return ids;
        }, []);

        const nextNodes = previous.map((node) => {
          if (!node.data.lenses.includes(activeLens)) return node;
          const updated = updatedMap.get(node.id);
          if (!updated) return node;

          return {
            ...node,
            ...updated,
            position: updated.position ?? node.position,
            data: {
              ...node.data,
              ...updated.data,
              positionsByLens: {
                ...node.data.positionsByLens,
                [activeLens]: updated.position ?? node.position,
              },
            },
          };
        });

        return autoLayoutChildren(nextNodes, movedParentIds);
      });
    },
    [activeLens, autoLayoutChildren]
  );

  const onEdgesChange = React.useCallback((changes: EdgeChange[]) => {
    setAllEdges((previous) => applyEdgeChanges(changes, previous));
  }, []);

  const createNodeData: (
    overrides: Partial<KpiNodeData>,
    position: { x: number; y: number }
  ) => KpiNodeData = React.useCallback(
    (
      overrides: Partial<KpiNodeData>,
      position: { x: number; y: number }
    ): KpiNodeData => ({
      label: "New KPI",
      target: undefined,
      targetHistory: {},
      actual: undefined,
      actualHistory: {},
      unit: "",
      frequency: "weekly",
      scope: "campus",
      performanceDirection: "higher_is_better",
      supportsCumulative: false,
      eventDate: "",
      definition: "Describe what this KPI is intended to measure.",
      measurement: "Describe exactly how this KPI is counted or calculated.",
      owner: "Assign an owner",
      dataSource: "Document the source system or report",
      updateNotes: "Add any timing, validation, or reporting notes here.",
      initiatives: [],
      tasks: [],
      lenses: [activeLens],
      lensLabels: [activeLens],
      positionsByLens: { [activeLens]: position },
      collapsedInLenses: [],
      status: "neutral",
      isRollup: false,
      metricsEditable: campus !== "All",
      metricNote:
        campus === "All"
          ? "Viewing all campuses combined. Switch to a campus to edit values."
          : undefined,
      statusNote: undefined,
      rollupWarning: undefined,
      childCount: 0,
      childrenCollapsed: false,
      onLabelChange: noopLabelChange,
      onMetricChange: noopMetricChange,
      onConfigChange: noopConfigChange,
      onDetailChange: noopDetailChange,
      onToggleRollup: noopToggleRollup,
      onAddChild: noopAddChild,
      onToggleChildren: noopToggleChildren,
      ...overrides,
    }),
    [activeLens, campus]
  );

  const onAddChild = React.useCallback(
    (parentId: string) => {
      const parent = allNodes.find((node) => node.id === parentId);
      if (!parent) return;

      const childId = uuidv4();
      const siblings = allEdges.filter((edge) => edge.target === parentId).length;
      const influenceInput = window.prompt("Influence %?", "20");
      const influence =
        influenceInput && influenceInput.trim() !== ""
          ? Number(influenceInput)
          : undefined;
      const position = {
        x: (parent.data.positionsByLens[activeLens]?.x ?? parent.position.x) - 250 + siblings * 300,
        y: (parent.data.positionsByLens[activeLens]?.y ?? parent.position.y) + 260,
      };

      const newNode: Node<KpiNodeData> = {
        id: childId,
        type: "kpi",
        position,
        data: createNodeData(
          {
            frequency: parent.data.frequency,
            scope: parent.data.scope,
          },
          position
        ),
      };

      const newEdge = makeEdge(
        `e-${childId}-${parentId}-${uuidv4()}`,
        childId,
        parentId,
        influence
      );

      const nextEdges = [...allEdges, newEdge];
      setAllEdges(nextEdges);
      setAllNodes((previous) =>
        autoLayoutChildren(
          applyContextAndRecalculate([...previous, newNode], nextEdges),
          [parentId]
        )
      );
      setActiveKpiId(childId);
    },
    [activeLens, allEdges, allNodes, applyContextAndRecalculate, autoLayoutChildren, createNodeData]
  );

  const onAddRootKpi = React.useCallback(() => {
    const lensNodes = allNodes.filter((node) => node.data.lenses.includes(activeLens));
    const lensNodeIds = new Set(lensNodes.map((node) => node.id));
    const lensEdges = allEdges.filter(
      (edge) => lensNodeIds.has(edge.source) && lensNodeIds.has(edge.target)
    );
    const rootCount = lensNodes.filter(
      (node) => !lensEdges.some((edge) => edge.target === node.id)
    ).length;
    const rootId = uuidv4();
    const viewportCenter = reactFlowInstance
      ? reactFlowInstance.screenToFlowPosition({
          x: window.innerWidth / 2,
          y: window.innerHeight / 2,
        })
      : null;
    const position = {
      x:
        viewportCenter?.x ??
        180 + rootCount * 340,
      y:
        viewportCenter?.y ??
        120,
    };

    const newNode: Node<KpiNodeData> = {
      id: rootId,
      type: "kpi",
      position,
      data: createNodeData({}, position),
    };

    setAllNodes((previous) =>
      applyContextAndRecalculate([...previous, newNode], allEdges)
    );
    setActiveKpiId(rootId);
  }, [
    activeLens,
    allEdges,
    allNodes,
    applyContextAndRecalculate,
    createNodeData,
    reactFlowInstance,
  ]);

  const onReferenceDateStep = React.useCallback(
    (direction: "previous" | "next") => {
      const nextReferenceDate = shiftReferenceDate(referenceDate, direction);
      setReferenceDate(nextReferenceDate);
      setAllNodes((previous) =>
        applyContextAndRecalculate(previous, allEdges, nextReferenceDate, campus)
      );
    },
    [allEdges, applyContextAndRecalculate, campus, referenceDate]
  );

  const onCampusChange = React.useCallback(
    (nextCampus: Campus) => {
      setCampus(nextCampus);
      setAllNodes((previous) =>
        applyContextAndRecalculate(previous, allEdges, referenceDate, nextCampus)
      );
    },
    [allEdges, applyContextAndRecalculate, referenceDate]
  );

  const onCumulativeViewChange = React.useCallback(
    (nextValue: boolean) => {
      setIsCumulativeView(nextValue);
      setAllNodes((previous) =>
        applyContextAndRecalculate(previous, allEdges, referenceDate, campus, activeLens)
      );
    },
    [activeLens, allEdges, applyContextAndRecalculate, campus, referenceDate]
  );

  const onLensChange = React.useCallback(
    (nextLens: Lens) => {
      setActiveLens(nextLens);
      setActiveKpiId(null);
      setAllNodes((previous) =>
        applyContextAndRecalculate(previous, allEdges, referenceDate, campus, nextLens)
      );
    },
    [allEdges, applyContextAndRecalculate, campus, referenceDate]
  );

  const onAddLens = React.useCallback(() => {
    setNewLensForm({
      name: "",
      scopeType: "organization",
      scopeId: "",
      description: "",
    });
    setIsCreateLensOpen(true);
  }, []);

  const onCreateLens = React.useCallback(() => {
    const trimmedName = newLensForm.name.trim();
    const trimmedScopeId =
      newLensForm.scopeId.trim() || getDefaultScopeId(newLensForm.scopeType);
    if (!trimmedScopeId) return;

    const lensId = uuidv4();
    const lensName = trimmedName || trimmedScopeId;

    setAvailableLenses((previous) => [...previous, lensId]);
    setLensConfigs((previous) => ({
      ...previous,
      [lensId]: {
        name: lensName,
        scopeType: newLensForm.scopeType,
        scopeId: trimmedScopeId,
        description:
          newLensForm.description.trim() || "Describe the purpose and boundaries of this lens.",
      },
    }));
    setLensNameDrafts((previous) => ({
      ...previous,
      [lensId]: lensName,
    }));
    setIsCreateLensOpen(false);
    onLensChange(lensId);
    setActiveLensDetailId(lensId);
  }, [newLensForm, onLensChange]);

  const onLensConfigChange = React.useCallback(
    (lens: Lens, field: keyof Omit<LensConfig, "name">, value: string) => {
      setLensConfigs((previous) => ({
        ...previous,
        [lens]: {
          ...previous[lens],
          [field]: value,
        },
      }));
    },
    []
  );

  const onLensDraftChange = React.useCallback((lens: Lens, value: string) => {
    setLensNameDrafts((previous) => ({
      ...previous,
      [lens]: value,
    }));
  }, []);

  const onRenameLens = React.useCallback(
    (currentLens: Lens, nextLensValue: string) => {
      const trimmed = nextLensValue.trim();
      const currentName = lensConfigs[currentLens]?.name ?? currentLens;
      if (!trimmed || trimmed === currentName) {
        setLensNameDrafts((previous) => ({
          ...previous,
          [currentLens]: currentName,
        }));
        return;
      }
      if (
        Object.entries(lensConfigs).some(
          ([lensId, config]) => lensId !== currentLens && config.name === trimmed
        )
      ) {
        setLensNameDrafts((previous) => ({
          ...previous,
          [currentLens]: currentName,
        }));
        return;
      }
      setLensConfigs((previous) => ({
        ...previous,
        [currentLens]: {
          ...previous[currentLens],
          name: trimmed,
        },
      }));
      setLensNameDrafts((previous) => ({
        ...previous,
        [currentLens]: trimmed,
      }));
    },
    [
      lensConfigs,
    ]
  );

  const onDeleteLens = React.useCallback(
    (lensToDelete: Lens) => {
      if (availableLenses.length <= 1) return;

      const nextAvailableLenses = availableLenses.filter((lens) => lens !== lensToDelete);
      const nextActiveLens =
        activeLens === lensToDelete ? nextAvailableLenses[0] : activeLens;

      setAvailableLenses(nextAvailableLenses);
      setLensConfigs((previous) => {
        const nextConfigs = { ...previous };
        delete nextConfigs[lensToDelete];
        return nextConfigs;
      });
      setLensNameDrafts((previous) => {
        const nextDrafts = { ...previous };
        delete nextDrafts[lensToDelete];
        return nextDrafts;
      });
      if (activeLens === lensToDelete) {
        setActiveLens(nextActiveLens);
        setActiveKpiId(null);
      }
      if (activeLensDetailId === lensToDelete) {
        setActiveLensDetailId(null);
      }

      let nextEdgesSnapshot = allEdges;
      setAllNodes((previous) => {
        const nextNodes = previous
          .map((node) => {
            if (!node.data.lenses.includes(lensToDelete)) return node;

            const nextPositionsByLens = { ...node.data.positionsByLens };
            delete nextPositionsByLens[lensToDelete];
            const nextLenses = node.data.lenses.filter((lens) => lens !== lensToDelete);

            if (nextLenses.length === 0) return null;

            return {
              ...node,
              data: {
                ...node.data,
                lenses: nextLenses,
                positionsByLens: nextPositionsByLens,
              },
            };
          })
          .filter((node): node is Node<KpiNodeData> => node !== null);

        const remainingNodeIds = new Set(nextNodes.map((node) => node.id));
        nextEdgesSnapshot = allEdges.filter(
          (edge) => remainingNodeIds.has(edge.source) && remainingNodeIds.has(edge.target)
        );
        setAllEdges(nextEdgesSnapshot);

        return applyContextAndRecalculate(
          nextNodes,
          nextEdgesSnapshot,
          referenceDate,
          campus,
          nextActiveLens
        );
      });
    },
    [
      activeLens,
      activeLensDetailId,
      allEdges,
      applyContextAndRecalculate,
      availableLenses,
      campus,
      referenceDate,
    ]
  );

  const onEdgeClick = React.useCallback(
    (_event: React.MouseEvent, edge: Edge) => {
      const currentInfluence = edge.data?.influence;
      const influenceInput = window.prompt(
        "Update influence %",
        typeof currentInfluence === "number" ? String(currentInfluence) : ""
      );
      if (influenceInput === null) return;

      const trimmed = influenceInput.trim();
      const influence = trimmed === "" ? undefined : Number(trimmed);
      const nextEdges = allEdges.map((current) =>
        current.id === edge.id
          ? makeEdge(current.id, current.source, current.target, influence)
          : current
      );
      setAllEdges(nextEdges);
      setAllNodes((previous) => applyContextAndRecalculate(previous, nextEdges));
    },
    [allEdges, applyContextAndRecalculate]
  );

  const onConnect = React.useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;

      const influenceInput = window.prompt("Influence %?", "20");
      if (influenceInput === null) return;

      const trimmed = influenceInput.trim();
      const influence = trimmed === "" ? undefined : Number(trimmed);
      const nextEdge = makeEdge(
        `e-${connection.source}-${connection.target}-${uuidv4()}`,
        connection.source,
        connection.target,
        influence
      );
      const nextEdges = [...allEdges, nextEdge];

      setAllEdges(nextEdges);
      setAllNodes((previous) => applyContextAndRecalculate(previous, nextEdges));
    },
    [allEdges, applyContextAndRecalculate]
  );

  const onNodeDoubleClick = React.useCallback(
    (_event: React.MouseEvent, node: Node<KpiNodeData>) => {
      setActiveKpiId(node.id);
    },
    []
  );

  const onLibraryDragStart = (event: React.DragEvent, nodeId: string) => {
    event.dataTransfer.setData("application/x-lens-kpi-id", nodeId);
    event.dataTransfer.effectAllowed = "move";
  };

  const onCanvasDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const onCanvasDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const nodeId = event.dataTransfer.getData("application/x-lens-kpi-id");
    if (!nodeId) return;

    const dropPosition = reactFlowInstance
      ? reactFlowInstance.screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        })
      : { x: 220, y: 180 };

    setAllNodes((previous) =>
      applyContextAndRecalculate(
        previous.map((node) => {
          if (node.id !== nodeId) return node;

          const nextLenses = node.data.lenses.includes(activeLens)
            ? node.data.lenses
            : [...node.data.lenses, activeLens];

          return {
            ...node,
            position: dropPosition,
            data: {
              ...node.data,
              lenses: nextLenses,
              positionsByLens: {
                ...node.data.positionsByLens,
                [activeLens]: dropPosition,
              },
            },
          };
        }),
        allEdges
      )
    );
  };

  const onRemoveFromActiveLens = React.useCallback(
    (nodeId: string) => {
      const targetNode = allNodes.find((node) => node.id === nodeId);
      if (!targetNode) return;

      setActiveKpiId(null);

      const becomesUnattached = targetNode.data.lenses.length <= 1;
      const nextEdges = becomesUnattached
        ? allEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
        : allEdges;

      if (becomesUnattached) {
        setAllEdges(nextEdges);
      }

      setAllNodes((previous) =>
        applyContextAndRecalculate(
          previous.map((node) => {
            if (node.id !== nodeId) return node;

            const nextPositionsByLens = { ...node.data.positionsByLens };
            delete nextPositionsByLens[activeLens];

            return {
              ...node,
              data: {
                ...node.data,
                lenses: node.data.lenses.filter((lens) => lens !== activeLens),
                positionsByLens: nextPositionsByLens,
              },
            };
          }),
          nextEdges
        )
      );
    },
    [activeLens, allEdges, allNodes, applyContextAndRecalculate]
  );

  const onDeleteKpi = React.useCallback(
    (nodeId: string) => {
      setActiveKpiId((current) => (current === nodeId ? null : current));
      setAllEdges((previous) =>
        previous.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
      );
      setAllNodes((previous) =>
        applyContextAndRecalculate(
          previous.filter((node) => node.id !== nodeId),
          allEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
        )
      );
    },
    [allEdges, applyContextAndRecalculate]
  );

  const onToggleChildren = React.useCallback(
    (nodeId: string) => {
      updateNodeById(nodeId, (node) => {
        const isCollapsed = node.data.collapsedInLenses.includes(activeLens);

        return {
          ...node,
          data: {
            ...node.data,
            collapsedInLenses: isCollapsed
              ? node.data.collapsedInLenses.filter((lens) => lens !== activeLens)
              : [...node.data.collapsedInLenses, activeLens],
          },
        };
      });

      const node = allNodes.find((item) => item.id === nodeId);
      const isCollapsed = node?.data.collapsedInLenses.includes(activeLens);
      if (isCollapsed) {
        setAllNodes((previous) => autoLayoutChildren(previous, [nodeId]));
      }
    },
    [activeLens, allNodes, autoLayoutChildren, updateNodeById]
  );

  function wireHandlers(nodes: Node<KpiNodeData>[]): Node<KpiNodeData>[] {
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        lensLabels: node.data.lenses.map((lensId) => lensConfigs[lensId]?.name ?? lensId),
        onLabelChange,
        onMetricChange,
        onConfigChange,
        onDetailChange,
        onToggleRollup,
        onAddChild,
        onToggleChildren,
      },
    }));
  }

  const lensNodes = React.useMemo(
    () => allNodes.filter((node) => node.data.lenses.includes(activeLens)),
    [activeLens, allNodes]
  );

  const lensEdges = React.useMemo(
    () => {
      const lensNodeIds = new Set(lensNodes.map((node) => node.id));
      return allEdges.filter(
        (edge) => lensNodeIds.has(edge.source) && lensNodeIds.has(edge.target)
      );
    },
    [allEdges, lensNodes]
  );

  const visibilityMeta = React.useMemo(() => {
    const childIdsByParent = new Map<string, string[]>();
    const parentIdsByChild = new Map<string, string[]>();

    lensEdges.forEach((edge) => {
      if (!childIdsByParent.has(edge.target)) {
        childIdsByParent.set(edge.target, []);
      }
      childIdsByParent.get(edge.target)!.push(edge.source);

      if (!parentIdsByChild.has(edge.source)) {
        parentIdsByChild.set(edge.source, []);
      }
      parentIdsByChild.get(edge.source)!.push(edge.target);
    });

    const topLevelNodeIds = lensNodes
      .filter((node) => !parentIdsByChild.has(node.id))
      .map((node) => node.id);

    const visibleNodeIds = new Set<string>();
    const queue =
      topLevelNodeIds.length > 0
        ? [...topLevelNodeIds]
        : lensNodes.map((node) => node.id);

    while (queue.length > 0) {
      const nodeId = queue.shift()!;
      if (visibleNodeIds.has(nodeId)) continue;
      visibleNodeIds.add(nodeId);

      const node = lensNodes.find((item) => item.id === nodeId);
      if (!node || node.data.collapsedInLenses.includes(activeLens)) {
        continue;
      }

      (childIdsByParent.get(nodeId) ?? []).forEach((childId) => {
        if (!visibleNodeIds.has(childId)) {
          queue.push(childId);
        }
      });
    }

    return { visibleNodeIds, childIdsByParent };
  }, [activeLens, lensEdges, lensNodes]);

  const displayedNodes = React.useMemo(
    () =>
      wireHandlers(
        lensNodes
          .filter((node) => visibilityMeta.visibleNodeIds.has(node.id))
          .map((node) => ({
            ...node,
            position: node.data.positionsByLens[activeLens] ?? node.position,
            data: {
              ...node.data,
              childCount: (visibilityMeta.childIdsByParent.get(node.id) ?? []).length,
              childrenCollapsed: node.data.collapsedInLenses.includes(activeLens),
            },
          }))
      ),
    [activeLens, lensNodes, visibilityMeta, wireHandlers]
  );

  const displayedEdges = React.useMemo(
    () =>
      lensEdges.filter(
        (edge) =>
          visibilityMeta.visibleNodeIds.has(edge.source) &&
          visibilityMeta.visibleNodeIds.has(edge.target)
      ),
    [lensEdges, visibilityMeta]
  );

  const activeKpi = allNodes.find((node) => node.id === activeKpiId);
  const activeLensConfig = lensConfigs[activeLens];
  const activeLensDetail = activeLensDetailId ? lensConfigs[activeLensDetailId] : null;
  const selectedEnterprise =
    activeEnterpriseId === null
      ? null
      : enterprises.find((enterprise) => enterprise.id === activeEnterpriseId) ?? null;
  const activeKpiInLens = activeKpi ? activeKpi.data.lenses.includes(activeLens) : false;
  const campusOptions = React.useMemo(() => ["All", ...campusList], [campusList]);

  const selectedNodeIds = React.useMemo(
    () =>
      allNodes
        .filter((node) => node.data.lenses.includes(activeLens) && node.selected)
        .map((node) => node.id),
    [activeLens, allNodes]
  );

  const libraryNodes = React.useMemo(
    () =>
      allNodes
        .filter((node) => {
          if (kpiLibraryFilter === allKpisFilterValue) return true;
          if (kpiLibraryFilter === unattachedKpisFilterValue) {
            return node.data.lenses.length === 0;
          }
          return node.data.lenses.includes(kpiLibraryFilter);
        })
        .slice()
        .sort((left, right) => left.data.label.localeCompare(right.data.label)),
    [allKpisFilterValue, allNodes, kpiLibraryFilter, unattachedKpisFilterValue]
  );

  const lensSummaries = React.useMemo(
    () =>
      availableLenses.map((lens) => ({
        lens,
        name: lensConfigs[lens]?.name ?? lens,
        scopeType: lensConfigs[lens]?.scopeType,
        scopeId: lensConfigs[lens]?.scopeId,
        kpiCount: allNodes.filter((node) => node.data.lenses.includes(lens)).length,
        description: lensConfigs[lens]?.description ?? "",
      })),
    [allNodes, availableLenses, lensConfigs]
  );

  const toolbarMaxWidth = React.useMemo(() => {
    let reservedWidth = 120;
    if (!isLensLibraryCollapsed) reservedWidth += 352;
    if (!isReferenceLibraryCollapsed) reservedWidth += 304;
    if (!isLibraryCollapsed) reservedWidth += 352;
    return `calc(100% - ${reservedWidth}px)`;
  }, [isLensLibraryCollapsed, isLibraryCollapsed, isReferenceLibraryCollapsed]);

  const serializeWorkspace = React.useCallback(
    () => ({
      availableLenses,
      lensConfigs,
      activeLens,
      referenceDate: referenceDate.toISOString(),
      isCumulativeView,
      campus,
      campusList,
      departmentList,
      ownerList,
      isLensLibraryCollapsed,
      isReferenceLibraryCollapsed,
      isLibraryCollapsed,
      panelOffsets,
      allNodes: allNodes.map((node) => ({
        ...node,
        data: stripNodeDataForPersistence(node.data),
      })),
      allEdges,
    }),
    [
      activeLens,
      allEdges,
      allNodes,
      availableLenses,
      campus,
      campusList,
      departmentList,
      isLensLibraryCollapsed,
      isLibraryCollapsed,
      isReferenceLibraryCollapsed,
      isCumulativeView,
      lensConfigs,
      ownerList,
      panelOffsets,
      referenceDate,
    ]
  );

  const applyWorkspace = React.useCallback(
    (workspace?: Record<string, unknown>) => {
      const nextWorkspace = workspace ?? {};
      const nextAvailableLenses =
        Array.isArray(nextWorkspace.availableLenses) && nextWorkspace.availableLenses.length > 0
          ? (nextWorkspace.availableLenses as Lens[])
          : initialLenses;
      const nextLensConfigs =
        nextWorkspace.lensConfigs &&
        typeof nextWorkspace.lensConfigs === "object" &&
        !Array.isArray(nextWorkspace.lensConfigs)
          ? (nextWorkspace.lensConfigs as LensConfigs)
          : initialLensConfigs;
      const nextActiveLens =
        typeof nextWorkspace.activeLens === "string" &&
        nextAvailableLenses.includes(nextWorkspace.activeLens as Lens)
          ? (nextWorkspace.activeLens as Lens)
          : nextAvailableLenses[0];
      const nextReferenceDate =
        typeof nextWorkspace.referenceDate === "string"
          ? new Date(nextWorkspace.referenceDate)
          : initialReferenceDate;
      const resolvedReferenceDate = Number.isNaN(nextReferenceDate.getTime())
        ? initialReferenceDate
        : nextReferenceDate;
      const nextCampus =
        typeof nextWorkspace.campus === "string" ? nextWorkspace.campus : defaultCampus;

      const nextNodes =
        Array.isArray(nextWorkspace.allNodes)
          ? (nextWorkspace.allNodes as Node<Partial<KpiNodeData>>[]).map((node) => ({
              ...node,
              data: hydrateNodeDataFromPersistence(node.data ?? {}),
            }))
          : defaultNodes;
      const nextEdges =
        Array.isArray(nextWorkspace.allEdges)
          ? (nextWorkspace.allEdges as Edge[])
          : defaultEdges;

      setAvailableLenses(nextAvailableLenses);
      setLensConfigs(nextLensConfigs);
      setActiveLens(nextActiveLens);
      setReferenceDate(resolvedReferenceDate);
      setIsCumulativeView(Boolean(nextWorkspace.isCumulativeView));
      setCampus(nextCampus);
      setCampusList(
        Array.isArray(nextWorkspace.campusList) && nextWorkspace.campusList.length > 0
          ? (nextWorkspace.campusList as string[])
          : initialCampuses
      );
      setDepartmentList(
        Array.isArray(nextWorkspace.departmentList) && nextWorkspace.departmentList.length > 0
          ? (nextWorkspace.departmentList as string[])
          : initialDepartments
      );
      setOwnerList(
        Array.isArray(nextWorkspace.ownerList) && nextWorkspace.ownerList.length > 0
          ? (nextWorkspace.ownerList as string[])
          : initialOwners
      );
      setIsLensLibraryCollapsed(true);
      setIsReferenceLibraryCollapsed(true);
      setIsLibraryCollapsed(true);
      setPanelOffsets(
        nextWorkspace.panelOffsets &&
          typeof nextWorkspace.panelOffsets === "object" &&
          !Array.isArray(nextWorkspace.panelOffsets)
          ? ({
              ...(nextWorkspace.panelOffsets as Record<FloatingPanelKey, { x: number; y: number }>),
              lens: { x: 0, y: 0 },
              reference: { x: 0, y: 0 },
              kpi: { x: 0, y: 0 },
            } as Record<FloatingPanelKey, { x: number; y: number }>)
          : { lens: { x: 0, y: 0 }, reference: { x: 0, y: 0 }, kpi: { x: 0, y: 0 } }
      );
      setAllNodes(
        applyContextAndRecalculate(
          nextNodes,
          nextEdges,
          resolvedReferenceDate,
          nextCampus,
          nextActiveLens
        )
      );
      setAllEdges(nextEdges);
      setActiveKpiId(null);
      setActiveLensDetailId(null);
    },
    [applyContextAndRecalculate, defaultEdges, defaultNodes]
  );

  React.useEffect(() => {
    applyWorkspaceRef.current = applyWorkspace;
  }, [applyWorkspace]);

  const onCreateEnterprise = React.useCallback(async () => {
    const nameInput = window.prompt("Enterprise name?", "");
    const trimmedName = nameInput?.trim();
    if (!trimmedName) return;

    try {
      const response = await api.post("/enterprises/", { name: trimmedName });
      const nextEnterprise = response.data as EnterpriseSummary;
      setEnterprises((previous) =>
        [...previous, nextEnterprise].sort((left, right) => left.name.localeCompare(right.name))
      );
      hasLoadedEnterpriseRef.current = false;
      setActiveEnterpriseId(nextEnterprise.id);
      applyWorkspace({});
    } catch (error) {
      console.error("Failed to create enterprise", error);
    }
  }, [applyWorkspace]);

  const onDeleteEnterprise = React.useCallback(
    async (enterprise: EnterpriseSummary) => {
      const confirmed = window.confirm(
        `Are you sure you want to delete ${enterprise.name}? All lenses, KPIs, and saved data associated with this enterprise will be lost.`
      );
      if (!confirmed) return;

      try {
        await api.delete(`/enterprises/${enterprise.id}`);
        setEnterprises((previous) => {
          const nextEnterprises = previous.filter((item) => item.id !== enterprise.id);

          if (activeEnterpriseId === enterprise.id) {
            hasLoadedEnterpriseRef.current = false;
            if (nextEnterprises.length > 0) {
              setActiveEnterpriseId(nextEnterprises[0].id);
            } else {
              setActiveEnterpriseId(null);
            }
          }

          return nextEnterprises;
        });
      } catch (error) {
        console.error("Failed to delete enterprise", error);
      }
    },
    [activeEnterpriseId]
  );

  React.useEffect(() => {
    const loadEnterprises = async () => {
      setIsEnterpriseLoading(true);

      try {
        let response = await api.get("/enterprises/");
        let nextEnterprises = response.data as EnterpriseSummary[];

        if (nextEnterprises.length === 0) {
          const createResponse = await api.post("/enterprises/", {
            name: defaultEnterpriseName,
          });
          nextEnterprises = [createResponse.data as EnterpriseSummary];
        }

        setEnterprises(nextEnterprises);
        hasLoadedEnterpriseRef.current = false;
        setActiveEnterpriseId(nextEnterprises[0].id);
      } catch (error) {
        console.error("Failed to load enterprises", error);
      } finally {
        setIsEnterpriseLoading(false);
      }
    };

    void loadEnterprises();
  }, []);

  React.useEffect(() => {
    if (activeEnterpriseId === null) return;

    const loadWorkspace = async () => {
      try {
        const response = await api.get(`/enterprises/${activeEnterpriseId}/workspace`);
        applyWorkspaceRef.current(response.data.workspace);
        hasLoadedEnterpriseRef.current = true;
      } catch (error) {
        console.error("Failed to load workspace", error);
      }
    };

    void loadWorkspace();
  }, [activeEnterpriseId]);

  React.useEffect(() => {
    if (!hasLoadedEnterpriseRef.current || activeEnterpriseId === null) {
      return;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        await api.put(`/enterprises/${activeEnterpriseId}/workspace`, {
          workspace: serializeWorkspace(),
        });
      } catch (error) {
        console.error("Failed to save workspace", error);
      }
    }, 700);

    return () => window.clearTimeout(timeoutId);
  }, [
    activeEnterpriseId,
    allEdges,
    allNodes,
    availableLenses,
    campus,
    campusList,
    departmentList,
    isLensLibraryCollapsed,
    isLibraryCollapsed,
    isReferenceLibraryCollapsed,
    lensConfigs,
    ownerList,
    panelOffsets,
    referenceDate,
    serializeWorkspace,
  ]);

  React.useEffect(() => {
    setAllNodes((previous) =>
      applyContextAndRecalculate(previous, allEdges, referenceDate, campus, activeLens)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setAllNodes((previous) =>
      applyContextAndRecalculate(previous, allEdges, referenceDate, campus, activeLens)
    );
  }, [activeLens, allEdges, applyContextAndRecalculate, campus, isCumulativeView, referenceDate]);

  React.useEffect(() => {
    setLensNameDrafts((previous) => {
      const nextDrafts: Record<string, string> = {};
      availableLenses.forEach((lens) => {
        nextDrafts[lens] = previous[lens] ?? lensConfigs[lens]?.name ?? lens;
      });
      return nextDrafts;
    });
  }, [availableLenses, lensConfigs]);

  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== "Delete" &&
        event.key !== "Backspace"
      ) {
        return;
      }

      if (selectedNodeIds.length === 0 || isTypingTarget(event.target)) {
        return;
      }

      event.preventDefault();
      selectedNodeIds.forEach((nodeId) => {
        onRemoveFromActiveLens(nodeId);
      });
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onRemoveFromActiveLens, selectedNodeIds]);

  React.useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const dragState = panelDragRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }

      setPanelOffsets((previous) => ({
        ...previous,
        [dragState.panel]: {
          x: dragState.originX + (event.clientX - dragState.startX),
          y: dragState.originY + (event.clientY - dragState.startY),
        },
      }));
    };

    const endDrag = (event: PointerEvent) => {
      if (panelDragRef.current?.pointerId === event.pointerId) {
        panelDragRef.current = null;
        setDraggingPanel(null);
      }
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    window.addEventListener("pointercancel", endDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endDrag);
      window.removeEventListener("pointercancel", endDrag);
    };
  }, []);

  const onPanelDragStart = React.useCallback(
    (panel: FloatingPanelKey, event: React.PointerEvent<HTMLDivElement>) => {
      stopCanvasDrag(event);
      panelDragRef.current = {
        panel,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: panelOffsets[panel].x,
        originY: panelOffsets[panel].y,
      };
      setDraggingPanel(panel);
    },
    [panelOffsets]
  );

  const getPanelTransform = React.useCallback(
    (panel: FloatingPanelKey, collapsed: boolean) =>
      `${collapsed ? "translateX(calc(100% + 16px)) " : ""}translate(${panelOffsets[panel].x}px, ${panelOffsets[panel].y}px)`,
    [panelOffsets]
  );

  return (
    <div
      style={{
        height: "100vh",
        width: "100%",
        background: "#000",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 16,
          left: 16,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 8,
          flexWrap: "wrap",
          background: "rgba(255,255,255,0.96)",
          borderRadius: 14,
          border: "1px solid #d1d5db",
          padding: 8,
          boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          width: "fit-content",
          maxWidth: `min(${toolbarMaxWidth}, calc(100% - 32px))`,
        }}
      >
        <select
          className="nodrag nopan"
          value={selectedEnterprise ? String(selectedEnterprise.id) : ""}
          onChange={(e) => {
            hasLoadedEnterpriseRef.current = false;
            setActiveEnterpriseId(Number(e.target.value));
          }}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          disabled={isEnterpriseLoading || enterprises.length === 0}
          style={{
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 14,
            background: "#fff",
            color: "#111827",
            minWidth: 180,
            maxWidth: 220,
          }}
        >
          <option value="" disabled>
            Enterprise
          </option>
          {enterprises.map((enterprise) => (
            <option key={enterprise.id} value={String(enterprise.id)}>
              {enterprise.name}
            </option>
          ))}
        </select>

        <select
          className="nodrag nopan"
          value={activeLens}
          onChange={(e) => onLensChange(e.target.value)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 14,
            background: "#fff",
            color: "#111827",
            minWidth: 160,
            maxWidth: 220,
          }}
        >
          {availableLenses.map((lens) => (
            <option key={lens} value={lens}>
              {lensConfigs[lens]?.name ?? lens}
            </option>
          ))}
        </select>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "40px 1fr 40px",
            gap: 8,
            alignItems: "center",
            minWidth: 220,
          }}
        >
          <button
            className="nodrag nopan"
            type="button"
            onClick={() => onReferenceDateStep("previous")}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 14,
              background: "#fff",
              color: "#111827",
              cursor: "pointer",
            }}
          >
            {"<"}
          </button>
          <div
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 14,
              textAlign: "center",
              fontWeight: 700,
              color: "#111827",
              background: "#fff",
              lineHeight: 1.35,
            }}
          >
            <div>{formatReferenceDate(referenceDate)}</div>
            <div style={{ fontSize: 12, fontWeight: 500, color: "#4b5563" }}>
              {formatReferenceSummary(referenceDate)}
            </div>
          </div>
          <button
            className="nodrag nopan"
            type="button"
            onClick={() => onReferenceDateStep("next")}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "100%",
              boxSizing: "border-box",
              border: "1px solid #d1d5db",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 14,
              background: "#fff",
              color: "#111827",
              cursor: "pointer",
            }}
          >
            {">"}
          </button>
        </div>

        <select
          className="nodrag nopan"
          value={campus}
          onChange={(e) => onCampusChange(e.target.value as Campus)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            padding: "8px 10px",
            fontSize: 14,
            background: "#fff",
            color: "#111827",
            minWidth: 130,
            maxWidth: 170,
          }}
        >
          {campusOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            fontWeight: 700,
            color: "#111827",
            padding: "0 4px",
          }}
        >
          <input
            className="nodrag nopan"
            type="checkbox"
            checked={isCumulativeView}
            onChange={(e) => onCumulativeViewChange(e.target.checked)}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
          />
          Cumulative
        </label>

      </div>

      <div
        className="nodrag nopan"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          position: "absolute",
          top: 16,
          right: 16,
          zIndex: 11,
          display: "flex",
          gap: 8,
          alignItems: "center",
        }}
      >
        <button
          type="button"
          className="nodrag nopan"
          onClick={() => setIsLensLibraryCollapsed((previous) => !previous)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            border: "1px solid #d1d5db",
            borderRadius: 12,
            background: "rgba(255,255,255,0.98)",
            color: "#111827",
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          }}
        >
          {isLensLibraryCollapsed ? "Show Lenses" : "Hide Lenses"}
        </button>

        <button
          type="button"
          className="nodrag nopan"
          onClick={() => setIsReferenceLibraryCollapsed((previous) => !previous)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            border: "1px solid #d1d5db",
            borderRadius: 12,
            background: "rgba(255,255,255,0.98)",
            color: "#111827",
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          }}
        >
          {isReferenceLibraryCollapsed ? "Show Lists" : "Hide Lists"}
        </button>

        <button
          type="button"
          className="nodrag nopan"
          onClick={() => setIsLibraryCollapsed((previous) => !previous)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            border: "1px solid #d1d5db",
            borderRadius: 12,
            background: "rgba(255,255,255,0.98)",
            color: "#111827",
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          }}
        >
          {isLibraryCollapsed ? "Show KPIs" : "Hide KPIs"}
        </button>
      </div>

      <div
        className="nodrag nopan"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          position: "absolute",
          top: 64,
          right: 648,
          bottom: 16,
          width: 320,
          zIndex: 10,
          background: "rgba(255,255,255,0.96)",
          borderRadius: 18,
          border: "1px solid #d1d5db",
          boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          padding: 16,
          overflowY: "auto",
          transform: getPanelTransform("lens", isLensLibraryCollapsed),
          opacity: isLensLibraryCollapsed ? 0 : 1,
          pointerEvents: isLensLibraryCollapsed ? "none" : "auto",
          transition:
            draggingPanel === "lens"
              ? "none"
              : "transform 180ms ease, opacity 180ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 6,
            cursor: "move",
            userSelect: "none",
            touchAction: "none",
          }}
          onMouseDown={stopCanvasDrag}
          onPointerDown={(event) => onPanelDragStart("lens", event)}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#111827",
            }}
          >
            Lens Library
          </div>
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#4b5563",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          Review every lens, switch into one, update its title and scope, or remove it
          when it is no longer needed.
        </div>

        <button
          type="button"
          className="nodrag nopan"
          onClick={onAddLens}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
            background: "#111827",
            color: "#fff",
            cursor: "pointer",
            marginBottom: 14,
          }}
        >
          + Add Lens
        </button>

        <div
          style={{
            display: "grid",
            gap: 10,
          }}
        >
          {lensSummaries.map(({ lens, name, scopeType, scopeId, kpiCount, description }) => {
            const isActive = lens === activeLens;

            return (
              <div
                key={lens}
                style={{
                  border: isActive ? "2px solid #111827" : "1px solid #d1d5db",
                  borderRadius: 14,
                  padding: 12,
                  background: isActive ? "#f3f4f6" : "#fff",
                }}
              >
                <input
                  className="nodrag nopan"
                  value={lensNameDrafts[lens] ?? name}
                  onChange={(e) => onLensDraftChange(lens, e.target.value)}
                  onBlur={() => onRenameLens(lens, lensNameDrafts[lens] ?? name)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      onRenameLens(lens, lensNameDrafts[lens] ?? name);
                      (e.currentTarget as HTMLInputElement).blur();
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      onLensDraftChange(lens, name);
                      (e.currentTarget as HTMLInputElement).blur();
                    }
                  }}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                    background: "#fff",
                    marginBottom: 8,
                  }}
                />
                <div
                  style={{
                    fontSize: 12,
                    color: "#4b5563",
                    marginBottom: 8,
                    lineHeight: 1.45,
                  }}
                >
                  {formatEnumLabel(scopeType ?? "organization")} | {scopeId}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#4b5563",
                    marginBottom: 10,
                    lineHeight: 1.45,
                  }}
                >
                  {kpiCount} KPI{kpiCount === 1 ? "" : "s"} in this lens
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 10,
                    lineHeight: 1.45,
                  }}
                >
                  {description || "No lens description yet."}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => onLensChange(lens)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      flex: 1,
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: isActive ? "#111827" : "#fff",
                      color: isActive ? "#fff" : "#111827",
                      cursor: "pointer",
                    }}
                  >
                    {isActive ? "Current Lens" : "Open Lens"}
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => setActiveLensDetailId(lens)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "#fff",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    Details
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={availableLenses.length <= 1}
                    onClick={() => onDeleteLens(lens)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #dc2626",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background:
                        availableLenses.length <= 1 ? "#f9fafb" : "#fff5f5",
                      color: availableLenses.length <= 1 ? "#9ca3af" : "#b91c1c",
                      cursor: availableLenses.length <= 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className="nodrag nopan"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          position: "absolute",
          top: 64,
          right: 336,
          bottom: 16,
          width: 280,
          zIndex: 10,
          background: "rgba(255,255,255,0.96)",
          borderRadius: 18,
          border: "1px solid #d1d5db",
          boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          padding: 16,
          overflowY: "auto",
          transform: getPanelTransform("reference", isReferenceLibraryCollapsed),
          opacity: isReferenceLibraryCollapsed ? 0 : 1,
          pointerEvents: isReferenceLibraryCollapsed ? "none" : "auto",
          transition:
            draggingPanel === "reference"
              ? "none"
              : "transform 180ms ease, opacity 180ms ease, right 180ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 6,
            cursor: "move",
            userSelect: "none",
            touchAction: "none",
          }}
          onMouseDown={stopCanvasDrag}
          onPointerDown={(event) => onPanelDragStart("reference", event)}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#111827",
            }}
          >
            Lists
          </div>
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#4b5563",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          Maintain the shared lists used for campuses, departments, and owners.
        </div>

        <button
          type="button"
          className="nodrag nopan"
          onClick={() => void onCreateEnterprise()}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
            background: "#111827",
            color: "#fff",
            cursor: "pointer",
            marginBottom: 14,
          }}
        >
          + Add Enterprise
        </button>

        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: 14,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: "#111827",
              marginBottom: 10,
            }}
          >
            Enterprises
          </div>

          <div
            style={{
              display: "grid",
              gap: 8,
            }}
          >
            {enterprises.map((enterprise) => {
              const isActive = enterprise.id === activeEnterpriseId;

              return (
                <div
                  key={enterprise.id}
                  style={{
                    border: isActive ? "2px solid #111827" : "1px solid #d1d5db",
                    borderRadius: 10,
                    background: isActive ? "#f3f4f6" : "#fff",
                    padding: 8,
                    display: "grid",
                    gap: 8,
                  }}
                >
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => {
                      hasLoadedEnterpriseRef.current = false;
                      setActiveEnterpriseId(enterprise.id);
                    }}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      borderRadius: 8,
                      padding: "6px 8px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "transparent",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    {enterprise.name}
                  </button>
                  <button
                    type="button"
                    className="nodrag nopan"
                    disabled={enterprises.length <= 1}
                    onClick={() => void onDeleteEnterprise(enterprise)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #dc2626",
                      borderRadius: 8,
                      padding: "6px 8px",
                      fontSize: 12,
                      fontWeight: 700,
                      background: enterprises.length <= 1 ? "#f9fafb" : "#fff5f5",
                      color: enterprises.length <= 1 ? "#9ca3af" : "#b91c1c",
                      cursor: enterprises.length <= 1 ? "not-allowed" : "pointer",
                    }}
                  >
                    Delete Enterprise
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gap: 16,
          }}
        >
          <StringListEditor title="Campuses" items={campusList} onChange={setCampusList} />
          <StringListEditor title="Departments" items={departmentList} onChange={setDepartmentList} />
          <StringListEditor title="Owners" items={ownerList} onChange={setOwnerList} />
        </div>
      </div>

      <div
        className="nodrag nopan"
        onMouseDown={stopCanvasDrag}
        onPointerDown={stopCanvasDrag}
        style={{
          position: "absolute",
          top: 64,
          right: 16,
          bottom: 16,
          width: 320,
          zIndex: 10,
          background: "rgba(255,255,255,0.96)",
          borderRadius: 18,
          border: "1px solid #d1d5db",
          boxShadow: "0 10px 24px rgba(0,0,0,0.2)",
          padding: 16,
          overflowY: "auto",
          transform: getPanelTransform("kpi", isLibraryCollapsed),
          opacity: isLibraryCollapsed ? 0 : 1,
          pointerEvents: isLibraryCollapsed ? "none" : "auto",
          transition:
            draggingPanel === "kpi"
              ? "none"
              : "transform 180ms ease, opacity 180ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 6,
            cursor: "move",
            userSelect: "none",
            touchAction: "none",
          }}
          onMouseDown={stopCanvasDrag}
          onPointerDown={(event) => onPanelDragStart("kpi", event)}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#111827",
            }}
          >
            KPI Library
          </div>
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#4b5563",
            lineHeight: 1.5,
            marginBottom: 14,
          }}
        >
          Drag an existing KPI into the <strong>{activeLensConfig?.name ?? activeLens}</strong>{" "}
          lens, or open it to edit details.
        </div>

        <select
          className="nodrag nopan"
          value={kpiLibraryFilter}
          onChange={(e) => setKpiLibraryFilter(e.target.value)}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 13,
            background: "#fff",
            color: "#111827",
            marginBottom: 14,
          }}
        >
          <option value={allKpisFilterValue}>All KPIs</option>
          <option value={unattachedKpisFilterValue}>Unattached KPIs</option>
          {availableLenses.map((lens) => (
            <option key={lens} value={lens}>
              Attached To {lensConfigs[lens]?.name ?? lens}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="nodrag nopan"
          onClick={onAddRootKpi}
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            width: "100%",
            border: "1px solid #d1d5db",
            borderRadius: 12,
            padding: "10px 12px",
            fontSize: 13,
            fontWeight: 700,
            background: "#111827",
            color: "#fff",
            cursor: "pointer",
            marginBottom: 14,
          }}
        >
          + Add KPI
        </button>

        <div
          style={{
            display: "grid",
            gap: 10,
          }}
        >
          {libraryNodes.map((node) => {
            const inLens = node.data.lenses.includes(activeLens);

            return (
              <div
                key={node.id}
                className="nodrag nopan"
                draggable={!inLens}
                onDragStart={(event) => onLibraryDragStart(event, node.id)}
                onDoubleClick={() => setActiveKpiId(node.id)}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 14,
                  padding: 12,
                  background: inLens ? "#f3f4f6" : "#fff",
                  cursor: inLens ? "default" : "grab",
                }}
              >
                <div
                  style={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#111827",
                    marginBottom: 4,
                  }}
                >
                  {node.data.label}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#4b5563",
                    lineHeight: 1.45,
                    marginBottom: 8,
                  }}
                >
                  {toTitleCase(node.data.frequency)} | {toTitleCase(node.data.scope)} | Lenses:{" "}
                  {node.data.lenses.length > 0
                    ? node.data.lenses
                        .map((lensId) => lensConfigs[lensId]?.name ?? lensId)
                        .join(", ")
                    : "Unattached"}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: inLens ? "#374151" : "#1d4ed8",
                    marginBottom: 10,
                  }}
                >
                  {inLens ? "Already on this lens" : "Drag onto the canvas to add"}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => setActiveKpiId(node.id)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      flex: 1,
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "#fff",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    Details
                  </button>
                  {inLens ? (
                    <button
                      type="button"
                      className="nodrag nopan"
                      onClick={() => onRemoveFromActiveLens(node.id)}
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 700,
                        background: "#fff",
                        color: "#111827",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => onDeleteKpi(node.id)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #dc2626",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "#fff5f5",
                      color: "#b91c1c",
                      cursor: "pointer",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {displayedNodes.length === 0 ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "rgba(255,255,255,0.96)",
              border: "1px solid #d1d5db",
              borderRadius: 18,
              boxShadow: "0 16px 36px rgba(0,0,0,0.24)",
              padding: 24,
              textAlign: "center",
              maxWidth: 380,
              pointerEvents: "auto",
            }}
          >
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#111827",
                marginBottom: 8,
              }}
            >
              Start The {activeLensConfig?.name ?? activeLens} Lens
            </div>
            <div
              style={{
                fontSize: 14,
                color: "#4b5563",
                lineHeight: 1.5,
                marginBottom: 16,
              }}
            >
              Add a new root KPI or drag an existing KPI in from the library to build this
              strategy map.
            </div>
            <button
              type="button"
              className="nodrag nopan"
              onClick={onAddRootKpi}
              onMouseDown={stopCanvasDrag}
              onPointerDown={stopCanvasDrag}
              style={{
                border: "none",
                borderRadius: 12,
                padding: "10px 16px",
                fontSize: 14,
                fontWeight: 700,
                background: "#111827",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              Add First KPI
            </button>
          </div>
        </div>
      ) : null}

      {isCreateLensOpen ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            className="nodrag nopan"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "min(620px, 100%)",
              maxHeight: "min(78vh, 720px)",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #d1d5db",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              padding: 22,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 18,
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: "#111827",
                    marginBottom: 6,
                  }}
                >
                  Create Lens
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#4b5563",
                    lineHeight: 1.5,
                  }}
                >
                  Build a lens by giving it a title and assigning the organizational scope it applies to.
                </div>
              </div>
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setIsCreateLensOpen(false)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#111827",
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 4,
                  }}
                >
                  Scope Type
                </div>
                <select
                  className="nodrag nopan"
                  value={newLensForm.scopeType}
                  onChange={(e) =>
                    setNewLensForm((previous) => ({
                      ...previous,
                      scopeType: e.target.value as ScopeType,
                    }))
                  }
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    color: "#111827",
                    background: "#fff",
                  }}
                >
                  {scopeTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {formatEnumLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                {newLensForm.scopeType === "campus" || newLensForm.scopeType === "department" ? (
                  <>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        marginBottom: 4,
                      }}
                    >
                      Scope Id
                    </div>
                    <select
                      className="nodrag nopan"
                      value={newLensForm.scopeId}
                      onChange={(e) =>
                        setNewLensForm((previous) => ({
                          ...previous,
                          scopeId: e.target.value,
                        }))
                      }
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "10px 12px",
                        fontSize: 14,
                        color: "#111827",
                        background: "#fff",
                      }}
                    >
                      <option value="">Select...</option>
                      {(newLensForm.scopeType === "campus" ? campusList : departmentList).map(
                        (option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        )
                      )}
                    </select>
                  </>
                ) : (
                  <DetailField
                    label="Scope Id"
                    value={newLensForm.scopeId}
                    onChange={(value) =>
                      setNewLensForm((previous) => ({
                        ...previous,
                        scopeId: value,
                      }))
                    }
                  />
                )}
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gap: 16,
              }}
            >
              <DetailField
                label="Lens Title"
                value={newLensForm.name}
                onChange={(value) =>
                  setNewLensForm((previous) => ({
                    ...previous,
                    name: value,
                  }))
                }
              />
              <DetailField
                label="Description"
                value={newLensForm.description}
                multiline
                onChange={(value) =>
                  setNewLensForm((previous) => ({
                    ...previous,
                    description: value,
                  }))
                }
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginTop: 18,
              }}
            >
              <button
                type="button"
                className="nodrag nopan"
                onClick={onCreateLens}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "none",
                  borderRadius: 12,
                  padding: "10px 16px",
                  fontSize: 14,
                  fontWeight: 700,
                  background: "#111827",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Create Lens
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {activeLensDetailId && activeLensDetail ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            className="nodrag nopan"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "min(620px, 100%)",
              maxHeight: "min(78vh, 720px)",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #d1d5db",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              padding: 22,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 18,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 10 }}>
                  <DetailField
                    label="Lens Name"
                    value={lensNameDrafts[activeLensDetailId] ?? activeLensDetail.name}
                    onChange={(value) => onLensDraftChange(activeLensDetailId, value)}
                    onBlur={() =>
                      onRenameLens(
                        activeLensDetailId,
                        lensNameDrafts[activeLensDetailId] ?? activeLensDetail.name
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        onRenameLens(
                          activeLensDetailId,
                          lensNameDrafts[activeLensDetailId] ?? activeLensDetail.name
                        );
                        (
                          e.currentTarget as HTMLInputElement | HTMLTextAreaElement
                        ).blur();
                      }
                      if (e.key === "Escape") {
                        e.preventDefault();
                        onLensDraftChange(activeLensDetailId, activeLensDetail.name);
                        (
                          e.currentTarget as HTMLInputElement | HTMLTextAreaElement
                        ).blur();
                      }
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#4b5563",
                    lineHeight: 1.5,
                  }}
                >
                  {lensSummaries.find((item) => item.lens === activeLensDetailId)?.kpiCount ?? 0}{" "}
                  KPI
                  {(lensSummaries.find((item) => item.lens === activeLensDetailId)?.kpiCount ??
                    0) === 1
                    ? ""
                    : "s"}{" "}
                  currently assigned to this lens
                </div>
              </div>
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setActiveLensDetailId(null)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#111827",
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                gap: 12,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 4,
                  }}
                >
                  Scope Type
                </div>
                <select
                  className="nodrag nopan"
                  value={activeLensDetail.scopeType}
                  onChange={(e) =>
                    onLensConfigChange(activeLensDetailId, "scopeType", e.target.value)
                  }
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    color: "#111827",
                    background: "#fff",
                  }}
                >
                  {scopeTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {formatEnumLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                {activeLensDetail.scopeType === "campus" ||
                activeLensDetail.scopeType === "department" ? (
                  <>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        marginBottom: 4,
                      }}
                    >
                      Scope Id
                    </div>
                    <select
                      className="nodrag nopan"
                      value={activeLensDetail.scopeId}
                      onChange={(e) =>
                        onLensConfigChange(activeLensDetailId, "scopeId", e.target.value)
                      }
                      onMouseDown={stopCanvasDrag}
                      onPointerDown={stopCanvasDrag}
                      style={{
                        width: "100%",
                        boxSizing: "border-box",
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        padding: "10px 12px",
                        fontSize: 14,
                        color: "#111827",
                        background: "#fff",
                      }}
                    >
                      <option value="">Select...</option>
                      {(activeLensDetail.scopeType === "campus"
                        ? campusList
                        : departmentList
                      ).map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <DetailField
                    label="Scope Id"
                    value={activeLensDetail.scopeId}
                    onChange={(value) => onLensConfigChange(activeLensDetailId, "scopeId", value)}
                  />
                )}
              </div>
            </div>

            <DetailField
              label="Lens Description"
              value={activeLensDetail.description}
              multiline
              onChange={(value) => onLensConfigChange(activeLensDetailId, "description", value)}
            />
          </div>
        </div>
      ) : null}

      {activeKpi ? (
        <div
          className="nodrag nopan"
          onMouseDown={stopCanvasDrag}
          onPointerDown={stopCanvasDrag}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            className="nodrag nopan"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={stopCanvasDrag}
            onPointerDown={stopCanvasDrag}
            style={{
              width: "min(700px, 100%)",
              maxHeight: "min(82vh, 780px)",
              overflowY: "auto",
              background: "#fff",
              borderRadius: 18,
              border: "1px solid #d1d5db",
              boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
              padding: 22,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 18,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 10 }}>
                  <DetailField
                    label="KPI Name"
                    value={activeKpi.data.label}
                    onChange={(value) => activeKpi.data.onLabelChange(activeKpi.id, value)}
                  />
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: "#4b5563",
                    lineHeight: 1.5,
                  }}
                >
                  {toTitleCase(activeKpi.data.frequency)} | {toTitleCase(activeKpi.data.scope)} |{" "}
                  {activeKpi.data.performanceDirection === "lower_is_better"
                    ? "Lower is better"
                    : "Higher is better"}{" "}
                  | {activeKpi.data.supportsCumulative ? "Cumulative-capable" : "Period-only"}{" "}
                  | {activeKpi.data.eventDate ? `Event: ${activeKpi.data.eventDate}` : "Ongoing"}{" "}
                  | {activeKpi.data.isRollup ? "Rollup KPI" : "Direct KPI"}
                </div>
              </div>
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => setActiveKpiId(null)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 10,
                  background: "#fff",
                  color: "#111827",
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginBottom: 18,
              }}
            >
              {activeKpiInLens ? (
                <button
                  type="button"
                  className="nodrag nopan"
                  onClick={() => onRemoveFromActiveLens(activeKpi.id)}
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    background: "#fff",
                    color: "#111827",
                    padding: "8px 12px",
                    fontSize: 13,
                    fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Remove From {activeLensConfig?.name ?? activeLens}
              </button>
            ) : null}
              <button
                type="button"
                className="nodrag nopan"
                onClick={() => onDeleteKpi(activeKpi.id)}
                onMouseDown={stopCanvasDrag}
                onPointerDown={stopCanvasDrag}
                style={{
                  border: "1px solid #dc2626",
                  borderRadius: 10,
                  background: "#fff5f5",
                  color: "#b91c1c",
                  padding: "8px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Delete KPI
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                gap: 12,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <DetailField
                  label="Owner"
                  value={activeKpi.data.owner}
                  listId="owner-options"
                  onChange={(value) =>
                    activeKpi.data.onDetailChange(activeKpi.id, "owner", value)
                  }
                  />
                </div>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 4,
                  }}
                >
                  Performance Rule
                </div>
                <select
                  className="nodrag nopan"
                  value={activeKpi.data.performanceDirection}
                  onChange={(e) =>
                    activeKpi.data.onConfigChange(
                      activeKpi.id,
                      "performanceDirection",
                      e.target.value as PerformanceDirection
                    )
                  }
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    color: "#111827",
                    background: "#fff",
                  }}
                >
                  {performanceDirectionOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "lower_is_better" ? "Lower is better" : "Higher is better"}
                    </option>
                  ))}
                </select>
              </div>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  <input
                    className="nodrag nopan"
                    type="checkbox"
                    checked={activeKpi.data.supportsCumulative}
                    onChange={(e) =>
                      activeKpi.data.onConfigChange(
                        activeKpi.id,
                        "supportsCumulative",
                        e.target.checked
                      )
                    }
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                  />
                  Supports cumulative view
                </label>
                <div
                  style={{
                    fontSize: 12,
                    color: "#4b5563",
                    lineHeight: 1.45,
                    marginTop: 8,
                  }}
                >
                  Responds to the global cumulative toggle when enabled.
                </div>
              </div>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#6b7280",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: 4,
                  }}
                >
                  Event Date
                </div>
                <input
                  className="nodrag nopan"
                  type="date"
                  value={activeKpi.data.eventDate}
                  onChange={(e) =>
                    activeKpi.data.onDetailChange(activeKpi.id, "eventDate", e.target.value)
                  }
                  onMouseDown={stopCanvasDrag}
                  onPointerDown={stopCanvasDrag}
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "10px 12px",
                    fontSize: 14,
                    color: "#111827",
                    background: "#fff",
                  }}
                />
                <div
                  style={{
                    fontSize: 12,
                    color: "#4b5563",
                    lineHeight: 1.45,
                    marginTop: 8,
                  }}
                >
                  Leave blank for an ongoing KPI. Before this date, the KPI stays neutral and shows as upcoming.
                </div>
              </div>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <DetailField
                  label="Data Source"
                  value={activeKpi.data.dataSource}
                  onChange={(value) =>
                    activeKpi.data.onDetailChange(activeKpi.id, "dataSource", value)
                  }
                />
              </div>
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 14,
                }}
              >
                <DetailField
                  label="Current Context"
                  value={`${activeLensConfig?.name ?? activeLens} | ${formatReferenceDate(referenceDate)}${activeKpi.data.scope === "campus" ? ` | Campus: ${campus}` : " | Global KPI"}`}
                  onChange={() => {}}
                  readOnly
                />
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gap: 16,
              }}
            >
              <DetailField
                label="Definition"
                value={activeKpi.data.definition}
                multiline
                onChange={(value) =>
                  activeKpi.data.onDetailChange(activeKpi.id, "definition", value)
                }
              />
              <DetailField
                label="How It Is Measured"
                value={activeKpi.data.measurement}
                multiline
                onChange={(value) =>
                  activeKpi.data.onDetailChange(activeKpi.id, "measurement", value)
                }
              />
              <DetailField
                label="Update Notes"
                value={activeKpi.data.updateNotes}
                multiline
                onChange={(value) =>
                  activeKpi.data.onDetailChange(activeKpi.id, "updateNotes", value)
                }
              />
            </div>

            <div
              style={{
                marginTop: 22,
                display: "grid",
                gap: 18,
              }}
            >
              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        color: "#111827",
                      }}
                    >
                      KPI Tasks
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#4b5563",
                        lineHeight: 1.45,
                      }}
                    >
                      Assign tasks directly to this KPI outside of a specific initiative.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => onAddKpiTask(activeKpi.id)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "#fff",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    + Task
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gap: 12,
                  }}
                >
                  {activeKpi.data.tasks.length === 0 ? (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6b7280",
                      }}
                    >
                      No KPI tasks yet.
                    </div>
                  ) : null}

                  {activeKpi.data.tasks.map((task) => (
                    <div
                      key={task.id}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 12,
                        padding: 12,
                        background: "#fff",
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.4fr 1fr 150px auto",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <WorkItemField
                          value={task.title}
                          placeholder="Task name"
                          onChange={(value) =>
                            onTaskChange(activeKpi.id, task.id, "title", value)
                          }
                        />
                        <WorkItemField
                          value={task.owner}
                          placeholder="Owner"
                          listId="owner-options"
                          onChange={(value) =>
                            onTaskChange(activeKpi.id, task.id, "owner", value)
                          }
                        />
                        <input
                          className="nodrag nopan"
                          type="date"
                          value={task.dueDate}
                          onChange={(e) =>
                            onTaskChange(activeKpi.id, task.id, "dueDate", e.target.value)
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            color: "#111827",
                            background: "#fff",
                          }}
                        />
                        <button
                          type="button"
                          className="nodrag nopan"
                          onClick={() => onDeleteKpiTask(activeKpi.id, task.id)}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            border: "1px solid #dc2626",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            background: "#fff5f5",
                            color: "#b91c1c",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "160px 1fr",
                          gap: 8,
                        }}
                      >
                        <select
                          className="nodrag nopan"
                          value={task.status}
                          onChange={(e) =>
                            onTaskChange(activeKpi.id, task.id, "status", e.target.value)
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          {workStatusOptions.map((status) => (
                            <option key={status} value={status}>
                              {toTitleCase(status)}
                            </option>
                          ))}
                        </select>
                        <WorkItemField
                          value={task.notes}
                          placeholder="Notes"
                          onChange={(value) =>
                            onTaskChange(activeKpi.id, task.id, "notes", value)
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 800,
                        color: "#111827",
                      }}
                    >
                      Initiatives
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#4b5563",
                        lineHeight: 1.45,
                      }}
                    >
                      Group strategic work under initiatives, then assign tasks inside each one.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="nodrag nopan"
                    onClick={() => onAddInitiative(activeKpi.id)}
                    onMouseDown={stopCanvasDrag}
                    onPointerDown={stopCanvasDrag}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 10,
                      padding: "8px 10px",
                      fontSize: 13,
                      fontWeight: 700,
                      background: "#fff",
                      color: "#111827",
                      cursor: "pointer",
                    }}
                  >
                    + Initiative
                  </button>
                </div>

                <div
                  style={{
                    display: "grid",
                    gap: 12,
                  }}
                >
                  {activeKpi.data.initiatives.length === 0 ? (
                    <div
                      style={{
                        fontSize: 13,
                        color: "#6b7280",
                      }}
                    >
                      No initiatives yet.
                    </div>
                  ) : null}

                  {activeKpi.data.initiatives.map((initiative) => (
                    <div
                      key={initiative.id}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 12,
                        padding: 12,
                        background: "#fff",
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.4fr 1fr 150px 160px auto",
                          gap: 8,
                          alignItems: "center",
                        }}
                      >
                        <WorkItemField
                          value={initiative.title}
                          placeholder="Initiative name"
                          onChange={(value) =>
                            onInitiativeChange(activeKpi.id, initiative.id, "title", value)
                          }
                        />
                        <WorkItemField
                          value={initiative.owner}
                          placeholder="Owner"
                          listId="owner-options"
                          onChange={(value) =>
                            onInitiativeChange(activeKpi.id, initiative.id, "owner", value)
                          }
                        />
                        <input
                          className="nodrag nopan"
                          type="date"
                          value={initiative.dueDate}
                          onChange={(e) =>
                            onInitiativeChange(
                              activeKpi.id,
                              initiative.id,
                              "dueDate",
                              e.target.value
                            )
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            color: "#111827",
                            background: "#fff",
                          }}
                        />
                        <select
                          className="nodrag nopan"
                          value={initiative.status}
                          onChange={(e) =>
                            onInitiativeChange(
                              activeKpi.id,
                              initiative.id,
                              "status",
                              e.target.value
                            )
                          }
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            width: "100%",
                            boxSizing: "border-box",
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            color: "#111827",
                            background: "#fff",
                          }}
                        >
                          {workStatusOptions.map((status) => (
                            <option key={status} value={status}>
                              {toTitleCase(status)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="nodrag nopan"
                          onClick={() => onDeleteInitiative(activeKpi.id, initiative.id)}
                          onMouseDown={stopCanvasDrag}
                          onPointerDown={stopCanvasDrag}
                          style={{
                            border: "1px solid #dc2626",
                            borderRadius: 10,
                            padding: "8px 10px",
                            fontSize: 13,
                            fontWeight: 700,
                            background: "#fff5f5",
                            color: "#b91c1c",
                            cursor: "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>

                      <WorkItemField
                        value={initiative.summary}
                        placeholder="Initiative summary"
                        onChange={(value) =>
                          onInitiativeChange(activeKpi.id, initiative.id, "summary", value)
                        }
                      />

                      <div
                        style={{
                          borderTop: "1px solid #e5e7eb",
                          paddingTop: 10,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#111827",
                            }}
                          >
                            Initiative Tasks
                          </div>
                          <button
                            type="button"
                            className="nodrag nopan"
                            onClick={() => onAddInitiativeTask(activeKpi.id, initiative.id)}
                            onMouseDown={stopCanvasDrag}
                            onPointerDown={stopCanvasDrag}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "6px 10px",
                              fontSize: 12,
                              fontWeight: 700,
                              background: "#fff",
                              color: "#111827",
                              cursor: "pointer",
                            }}
                          >
                            + Task
                          </button>
                        </div>

                        {initiative.tasks.length === 0 ? (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#6b7280",
                            }}
                          >
                            No initiative tasks yet.
                          </div>
                        ) : null}

                        {initiative.tasks.map((task) => (
                          <div
                            key={task.id}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1.3fr 1fr 150px 160px auto",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <WorkItemField
                              value={task.title}
                              placeholder="Task name"
                              onChange={(value) =>
                                onInitiativeTaskChange(
                                  activeKpi.id,
                                  initiative.id,
                                  task.id,
                                  "title",
                                  value
                                )
                              }
                            />
                            <WorkItemField
                              value={task.owner}
                              placeholder="Owner"
                              listId="owner-options"
                              onChange={(value) =>
                                onInitiativeTaskChange(
                                  activeKpi.id,
                                  initiative.id,
                                  task.id,
                                  "owner",
                                  value
                                )
                              }
                            />
                            <input
                              className="nodrag nopan"
                              type="date"
                              value={task.dueDate}
                              onChange={(e) =>
                                onInitiativeTaskChange(
                                  activeKpi.id,
                                  initiative.id,
                                  task.id,
                                  "dueDate",
                                  e.target.value
                                )
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "8px 10px",
                                fontSize: 13,
                                color: "#111827",
                                background: "#fff",
                              }}
                            />
                            <select
                              className="nodrag nopan"
                              value={task.status}
                              onChange={(e) =>
                                onInitiativeTaskChange(
                                  activeKpi.id,
                                  initiative.id,
                                  task.id,
                                  "status",
                                  e.target.value
                                )
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                width: "100%",
                                boxSizing: "border-box",
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "8px 10px",
                                fontSize: 13,
                                color: "#111827",
                                background: "#fff",
                              }}
                            >
                              {workStatusOptions.map((status) => (
                                <option key={status} value={status}>
                                  {toTitleCase(status)}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              className="nodrag nopan"
                              onClick={() =>
                                onDeleteInitiativeTask(activeKpi.id, initiative.id, task.id)
                              }
                              onMouseDown={stopCanvasDrag}
                              onPointerDown={stopCanvasDrag}
                              style={{
                                border: "1px solid #dc2626",
                                borderRadius: 10,
                                padding: "8px 10px",
                                fontSize: 13,
                                fontWeight: 700,
                                background: "#fff5f5",
                                color: "#b91c1c",
                                cursor: "pointer",
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <datalist id="owner-options">
        {ownerList.map((owner) => (
          <option key={owner} value={owner} />
        ))}
      </datalist>

      <ReactFlowProvider>
        <ReactFlow
          nodes={displayedNodes}
          edges={displayedEdges}
          nodeTypes={nodeTypes}
          onInit={setReactFlowInstance}
          onConnect={onConnect}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgeClick={onEdgeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onDragOver={onCanvasDragOver}
          onDrop={onCanvasDrop}
          minZoom={0.05}
          maxZoom={2.5}
          fitView
        >
          <MiniMap />
          <Controls />
          <Background color="#444" gap={24} size={1} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  );
}
